import { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { MapPin, Phone, Loader, Mic, MicOff, VolumeX, MessageCircle, AudioLines, Bookmark, ExternalLink, Heart, Calendar, Home, ArrowUp, LogIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import api, { publicApi } from '../services/api';
import { useClientAuth } from '../context/ClientAuthContext';
import { clientAuthConfig, clientTokenKey } from '../utils/clientAuthConfig';
import { formatBuyerCriteriaLine } from '../utils/buyerProfileDisplay';
import { shortRemarks } from '../utils/cardText';
import { localIsoDate, otherTimeText } from '../utils/bookingDate';
import {
  buildReturningClientRecap,
  clearResumeRecap,
  resolveReturningClientStartIntent,
} from '../utils/chatResumeRecap';
import { parseAskPropertySearch } from '../utils/askPropertyChatPath';
import {
  wantsSpokenFiller,
  isSessionGoodbyePhrase,
  chatCardBadgeLabel,
} from '../utils/buyerChatTurn';
import { formatMessage } from '../utils/buyerMessageFormat';
import BuyerCriteriaWizard, { draftCriteriaStorageKey } from '../components/BuyerCriteriaWizard';
import {
  getStoredConversationId,
  setStoredConversationId,
  clearStoredConversationId,
  setHandsFreePreference,
  getHandsFreePreference,
  clearHandsFreePreference,
  clearLegacyHandsFreePreference,
  stashHandoff,
  takeHandoff,
  normalizeConvPathMode,
} from '../utils/buyerChatSession';
import {
  clearBuyerDemoSession,
  legalHref,
  syncTesterModeFromSearch,
  testerLandingHref,
} from '../utils/testerMode';
import CreaTrademarkNotice from '../components/CreaTrademarkNotice';
import EqualHousingNotice from '../components/EqualHousingNotice';
import './BuyerChat.css';
import './BuyerChat.evening.css';
import './BuyerChat.day.css';

async function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** GET public conversation with short retries (handoff / brief backend lag). */
async function fetchPublicConversation(conversationId, agentId, agentSlug, {
  attempts = 1,
  delaysMs = [0],
} = {}) {
  let lastErr = null;
  for (let i = 0; i < attempts; i += 1) {
    if (i > 0) await sleep(delaysMs[i - 1] ?? 700);
    try {
      return await publicApi.get(`/conversations/public/${conversationId}`, {
        params: { agent_id: agentId },
        headers: { Authorization: '' },
        ...clientAuthConfig(agentSlug),
      });
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // Don't burn retries on auth / ownership — those won't recover.
      if (status === 403 || status === 400) throw err;
    }
  }
  throw lastErr;
}

// Anonymous save/pass buffer — listings acted on before sign-in, synced to the
// account on register/login.
const savedBufferKey = (slug) => `a4a_saved_${slug}`;
function readSavedBuffer(slug) {
  try {
    return JSON.parse(localStorage.getItem(savedBufferKey(slug)) || '{}') || {};
  } catch {
    return {};
  }
}
function writeSavedBufferEntry(slug, key, value) {
  const b = readSavedBuffer(slug);
  b[key] = value;
  localStorage.setItem(savedBufferKey(slug), JSON.stringify(b));
}
function removeSavedBufferEntry(slug, key) {
  const b = readSavedBuffer(slug);
  delete b[key];
  localStorage.setItem(savedBufferKey(slug), JSON.stringify(b));
}
function clearSavedBuffer(slug) {
  localStorage.removeItem(savedBufferKey(slug));
}

// Theme preference. Not a save-card session flag (those are a4a_save_* / a4a_saved_*).
const BUYER_DAYLIGHT_KEY = 'a4a_buyer_daylight';

function readBuyerDaylight() {
  try {
    return localStorage.getItem(BUYER_DAYLIGHT_KEY) === 'day';
  } catch {
    return false;
  }
}

function writeBuyerDaylight(day) {
  try {
    localStorage.setItem(BUYER_DAYLIGHT_KEY, day ? 'day' : 'night');
  } catch { /* ignore */ }
}

function BuyerDaylightSwitch({ daylight, onToggle }) {
  return (
    <button
      type="button"
      className={`buyer-light-switch${daylight ? ' is-day' : ''}`}
      role="switch"
      aria-checked={daylight}
      aria-label={daylight ? 'Switch to night' : 'Switch to day'}
      onClick={onToggle}
    >
      <span className="buyer-light-switch__word buyer-light-switch__word--day" aria-hidden="true">Day</span>
      <span className="buyer-light-switch__plate" aria-hidden="true">
        <span className="buyer-light-switch__screw" />
        <span className="buyer-light-switch__rocker" />
        <span className="buyer-light-switch__screw" />
      </span>
      <span className="buyer-light-switch__word buyer-light-switch__word--night" aria-hidden="true">Night</span>
    </button>
  );
}

function savePromptDismissKey(agentSlug) {
  return `a4a_save_prompt_dismissed_${agentSlug}`;
}

function saveCardAutoShownKey(agentSlug) {
  return `a4a_save_card_auto_shown_${agentSlug}`;
}

function saveCardStrongReofferKey(agentSlug) {
  return `a4a_save_card_strong_reoffer_${agentSlug}`;
}

function clearSaveCardSessionFlags(agentSlug) {
  if (!agentSlug) return;
  try {
    sessionStorage.removeItem(savePromptDismissKey(agentSlug));
    sessionStorage.removeItem(saveCardAutoShownKey(agentSlug));
    sessionStorage.removeItem(saveCardStrongReofferKey(agentSlug));
  } catch { /* ignore */ }
}

/** Enough search signal to make a save-search offer meaningful. */
function hasMeaningfulSaveCriteria(criteria) {
  if (!criteria || typeof criteria !== 'object') return false;
  const hasBudget = criteria.price_min != null || criteria.price_max != null
    || criteria.budget_min != null || criteria.budget_max != null;
  const areas = criteria.areas;
  const hasArea = Array.isArray(areas)
    ? areas.length > 0
    : Boolean(areas && String(areas).trim());
  const types = criteria.property_types;
  const hasType = Array.isArray(types)
    ? types.length > 0
    : Boolean(types && String(types).trim());
  return hasBudget || hasArea || hasType;
}

function countListingsShownInThread(messages) {
  let n = 0;
  for (const m of messages || []) {
    if (m.role !== 'assistant') continue;
    if (Array.isArray(m.featured_properties)) n += m.featured_properties.length;
  }
  return n;
}

function assistantOfferedSaveSearch(msg) {
  if (!msg || msg.role !== 'assistant') return false;
  if (msg.offer_save_search) return true;
  const t = msg.content || '';
  return /\bwould you like to save (this|your) search\b/i.test(t)
    || /\bBefore you go\b.*\bsave (this|your) search\b/i.test(t)
    || /\bSave search at the top of the page\b/i.test(t)
    || /\bsave (my |this |your )?search\b[^.!?\n]{0,60}\bcard\b/i.test(t)
    || /\bcard\b[^.!?\n]{0,60}\bsave (my |this |your )?search\b/i.test(t);
}

function bookingRequestSent(text) {
  return /BOOKING_REQUEST:|sent your showing request|showing request (has been |was )?sent|\brequest sent\b/i.test(text);
}

function bookingIntakePending(messages) {
  const list = messages || [];
  // The save-search close is the line that points at the card. Intake is over.
  const lastAssistant = [...list].reverse().find((m) => m.role === 'assistant');
  if (assistantOfferedSaveSearch(lastAssistant)) return false;

  let lastOpen = -1;
  let lastConfirm = -1;
  list.forEach((m, i) => {
    const text = String(m.content || '');
    if (m.role === 'user' && /\b(book|schedule)\b/i.test(text) && /\bshowing\b/i.test(text)) {
      lastOpen = i;
    }
    const sent = bookingRequestSent(text) && (m.role === 'assistant' || m.role === 'system');
    if (sent) lastConfirm = i;
    if (
      m.role === 'assistant'
      && !sent
      && lastOpen > lastConfirm
      && i > lastOpen
      && /\b(full name|your name|phone number|your phone|email|preferred time|what time)\b/i.test(text)
    ) {
      lastOpen = i;
    }
  });
  return lastOpen > lastConfirm;
}

function formatSellerPropertyLine(profile) {
  if (!profile) return null;
  const parts = [profile.property_address, profile.property_city].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

const AI_AVATAR_PRIMARY = '/ava-avatar.png';
const AI_AVATAR_FALLBACK = '/alex-avatar.png';

function getAiAvatarSrc(agent) {
  const file = String(agent?.avatar_filename || '').trim();
  if (!file) return AI_AVATAR_PRIMARY;
  return file.startsWith('/') ? file : `/${file}`;
}

function getAgentPhotoSrc(agent) {
  if (!agent?.has_agent_photo || !agent?.id) return null;
  return `/api/auth/agent-photo/${agent.id}`;
}

function isHexColor(value) {
  return /^#[0-9a-fA-F]{6}$/.test(String(value || '').trim());
}

function hexToRgba(hex, alpha) {
  const h = String(hex || '').trim().replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return `rgba(201, 160, 90, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function handleAvatarFallback(event) {
  if (!event?.currentTarget) return;
  if (event.currentTarget.src.includes(AI_AVATAR_FALLBACK)) return;
  event.currentTarget.src = AI_AVATAR_FALLBACK;
}

const RETURNING_SUGGESTIONS = [
  'What homes fit my search?',
  'I want to update my budget or areas',
  "I'd like to book a showing",
];

// Tappable area choices, shown when Ava asks the location question. The pilot
// is scoped to St. John's and the surrounding areas, so these are the local
// communities only. Chips are shortcuts — typing or speaking always works.
const LOCAL_AREA_COMMUNITIES = [
  "St. John's",
  'Mount Pearl',
  'Paradise',
  'Conception Bay South',
  'Torbay',
  "Portugal Cove–St. Philip's",
  'Holyrood',
];

const returningSellerSuggestions = (agentFirstName) => [
  "What's happening in my area right now?",
  'I want to update my property details',
  `Set up a consultation with ${agentFirstName || 'the agent'}`,
];

function conversationNeedsBranchChoice(messages) {
  const users = (messages || []).filter((m) => m.role === 'user');
  if (users.length > 0) return false;
  const greeting = (messages || []).find((m) => m.role === 'assistant');
  const text = greeting?.content || '';
  return Boolean(greeting && (/tell me how I can help/i.test(text) || /buying or selling/i.test(text) || /If you're selling a home, I can help/i.test(text)));
}

function detectBranchFromText(text) {
  const t = String(text || '').trim().toLowerCase();
  if (/\b(sell(ing)?(\s+a\s+home)?|sell\s+my\s+home|looking\s+to\s+sell|list(ing)?\s+my|thinking\s+about\s+selling|want\s+to\s+sell)\b/.test(t)) {
    return 'seller';
  }
  if (/\b(buy(ing)?(\s+a\s+home)?|looking\s+to\s+buy|want\s+to\s+buy|purchase(\s+a\s+home)?|see\s+homes|show\s+me\s+homes|find\s+(me\s+)?a\s+home)\b/.test(t)) {
    return 'buyer';
  }
  return null;
}

/** Listing cards / browse chips — must open the buyer conversation, never seller. */
function isBuyerBrowseIntent(text, { mlsNumber = null, propertyId = null, pathIntent = null } = {}) {
  if (pathIntent === 'buyer') return true;
  // Featured cards always pass mls_number / property id — that alone is enough.
  if (mlsNumber) return true;
  if (propertyId) return true;
  const t = String(text || '').trim();
  if (!t) return false;
  // Do NOT treat bare "Tell me about {address}" as browse — sellers ask that
  // about comps in seller mode. Cards pass mlsNumber / pathIntent instead.
  if (/^see homes\b/i.test(t)) return true;
  if (/\b(show me|looking for|find me)\b[\s\S]{0,40}\b(homes?|houses?|listings?|properties)\b/i.test(t)) {
    return true;
  }
  if (/how does buying\b/i.test(t)) return true;
  return false;
}

function messageIsGoodbye(text) {
  return isSessionGoodbyePhrase(text);
}

function threadWindingDownFromMessages(list) {
  const rows = Array.isArray(list) ? list : [];
  const lastUser = [...rows].reverse().find((m) => m.role === 'user');
  const lastAi = [...rows].reverse().find((m) => m.role === 'assistant');
  return messageIsGoodbye(lastUser?.content) || messageIsGoodbye(lastAi?.content);
}

/** True when the message only states buy/sell intent — no property details yet. */
function isOnlyBranchIntent(text) {
  if (!detectBranchFromText(text)) return false;
  const normalized = String(text || '').toLowerCase()
    .replace(/[^\w\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const branchOnlyPatterns = [
    /^i am looking to sell( a home| my home)?$/,
    /^i'?m looking to sell( a home| my home)?$/,
    /^looking to sell( my home| a home)?$/,
    /^i want to sell( my home| a home)?$/,
    /^(i'?m )?thinking (about|of) selling( a home| my home)?$/,
    /^sell(ing)?( a home)?$/,
    /^sell my home$/,
    /^i am looking to buy( a home)?$/,
    /^i'?m looking to buy( a home)?$/,
    /^looking to buy( a home)?$/,
    /^i want to buy( a home)?$/,
    /^buy(ing)?( a home)?$/,
  ];
  if (branchOnlyPatterns.some((re) => re.test(normalized))) return true;

  const stripped = normalized
    .replace(/\b(i am|i'?m|we are|we'?re|hi|hello|hey)\b/g, '')
    .replace(/\b(looking to|thinking (about|of)|want to|interested in|hoping to)\b/g, '')
    .replace(/\b(sell(ing)?|buy(ing)?|purchase)\b/g, '')
    .replace(/\b(a|an|the|my|our|home|house|property|please|thanks)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped.length < 8;
}

function isSellerPathForkMessage(content) {
  const text = content || '';
  return /more information about selling/i.test(text)
    && /set up an appointment/i.test(text);
}

function conversationNeedsSellerPathChoice(messages) {
  const list = messages || [];
  const users = list.filter((m) => m.role === 'user');
  if (users.length > 1) return false;
  if (users.length === 1 && detectSellerPathFromText(users[0].content || '')) return false;
  const lastAssistant = [...list].reverse().find((m) => m.role === 'assistant');
  return isSellerPathForkMessage(lastAssistant?.content);
}

function detectSellerPathFromText(text) {
  const t = String(text || '').trim().toLowerCase();
  if (/\b(appointment|meet with|talk to|speak with|call me|schedule|consultation|book|set up|ready to list|list my home|get started)\b/.test(t)) {
    return 'appointment';
  }
  if (/\b(information|more info|learn about|tell me about|how does selling|selling process|prep|pricing|market|questions?)\b/.test(t)) {
    return 'info';
  }
  return null;
}

function sellerPathUserMessage(path, agentFirstName) {
  if (path === 'appointment') {
    return `I'd like to set up an appointment with ${agentFirstName || 'your agent'}`;
  }
  return "I'd like more information about selling";
}

function formatPropertyTypeLabel(type) {
  if (!type) return '';
  return String(type).replace(/_/g, ' ');
}

function cardPhotoUrl(p) {
  if (p?.photo_url) return p.photo_url;
  const list = Array.isArray(p?.photos) ? p.photos : [];
  const first = list[0];
  if (!first) return null;
  if (typeof first === 'string') return first;
  return first.url || first.src || first.href || null;
}

function cardPhotoList(p) {
  const list = (Array.isArray(p?.photos) ? p.photos : [])
    .map((x) => (typeof x === 'string' ? { url: x } : x))
    .filter((x) => x?.url);
  if (!list.length && p?.photo_url) return [{ url: p.photo_url }];
  return list;
}

/**
 * Every photo on the card itself (floor plans too), flipped with arrows, so
 * nobody has to ask for more photos. A lone card also gets thumbnails
 * (Lillian, 2026-10-25).
 */
function CardMedia({ p, thumbs = false, children }) {
  const photos = cardPhotoList(p);
  const [index, setIndex] = useState(0);
  const at = Math.min(index, Math.max(photos.length - 1, 0));
  const photo = photos[at];
  const many = photos.length > 1;
  const step = (d) => setIndex((at + d + photos.length) % photos.length);
  return (
    <>
      <div className="buyer-listing-card__media">
        {photo ? (
          <img
            className="buyer-listing-card__photo"
            src={photo.url}
            alt={[p.address, p.city].filter(Boolean).join(', ') + (photo.caption ? ` — ${photo.caption}` : '')}
            loading="lazy"
          />
        ) : (
          <div className="buyer-listing-card__photo buyer-listing-card__photo--placeholder">
            Photo coming soon
          </div>
        )}
        {many && (
          <>
            <button type="button" className="buyer-listing-card__nav buyer-listing-card__nav--prev" onClick={() => step(-1)} aria-label="Previous photo">
              <ChevronLeft size={18} />
            </button>
            <button type="button" className="buyer-listing-card__nav buyer-listing-card__nav--next" onClick={() => step(1)} aria-label="Next photo">
              <ChevronRight size={18} />
            </button>
            <span className="buyer-listing-card__count">
              {photo.caption ? `${photo.caption} · ` : ''}{at + 1} / {photos.length}
            </span>
          </>
        )}
        {children}
      </div>
      {thumbs && many && (
        <div className="buyer-listing-card__thumbs" role="group" aria-label="Photos">
          {photos.map((ph, i) => (
            <button
              key={ph.url}
              type="button"
              className={`buyer-listing-card__thumb${i === at ? ' is-on' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={ph.caption || `Photo ${i + 1}`}
              aria-pressed={i === at}
            >
              <img src={ph.url} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Official pages (town sites, NL government, market statistics), chosen by the server from a fixed list. */
function joinPlaces(names) {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * A button per town under Ava's line on each; tap several, then search them,
 * or "All of them" (Lillian, 2026-10-26).
 */
function AreaPickChips({ options, all, disabled, onSubmit }) {
  const [picked, setPicked] = useState([]);
  const toggle = (label) => setPicked((p) => (p.includes(label) ? p.filter((x) => x !== label) : [...p, label]));
  return (
    <div className="buyer-area-picker" role="group" aria-label="Choose areas">
      <div className="buyer-area-picker__hint">Tap the ones you&apos;d like — or type or say your answer</div>
      <div className="buyer-area-picker__chips">
        {options.map((o) => (
          <button
            key={o.label}
            type="button"
            className={`buyer-area-picker__chip${picked.includes(o.label) ? ' buyer-area-picker__chip--on' : ''}`}
            aria-pressed={picked.includes(o.label)}
            onClick={() => toggle(o.label)}
            disabled={disabled}
          >
            {o.label}
          </button>
        ))}
        {all?.send && (
          <button
            type="button"
            className="buyer-area-picker__chip"
            onClick={() => onSubmit(all.send)}
            disabled={disabled}
          >
            {all.label}
          </button>
        )}
      </div>
      {picked.length > 0 && (
        <button
          type="button"
          className="buyer-area-picker__go"
          onClick={() => onSubmit(`Let's look in ${joinPlaces(picked)}`)}
          disabled={disabled}
        >
          Search {joinPlaces(picked)}
        </button>
      )}
    </div>
  );
}

function AreaLinks({ links }) {
  return (
    <div className="buyer-area-links">
      {links.filter((l) => /^https:\/\//.test(l?.url || '')).map((l) => (
        <a
          key={l.url}
          className="buyer-area-links__link"
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${l.label || `${l.name} website`} (opens in a new tab)`}
        >
          {l.label || `${l.name} website`} <ExternalLink size={13} aria-hidden="true" />
        </a>
      ))}
    </div>
  );
}

function listingMapsHref(p) {
  if (p?.maps_url) return p.maps_url;
  const query = [p?.address, p?.city, p?.province || 'NL', 'Canada'].filter(Boolean).join(', ');
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

// Booking picker (Lillian, 2026-10-24): the agent's open times as buttons,
// then a typed contact form, so voice users never have digits misheard.
const PHONE_DIGITS_OK = (v) => {
  const d = String(v || '').replace(/\D/g, '');
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
};
const EMAIL_OK = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());

export function BookingPicker({ picker, agentFirstName, disabled = false, onSubmit }) {
  const [slot, setSlot] = useState(null);
  const [otherDate, setOtherDate] = useState('');
  const [otherPart, setOtherPart] = useState(null);
  const [otherNote, setOtherNote] = useState('');
  const [name, setName] = useState('');
  const [method, setMethod] = useState('phone');
  const [contact, setContact] = useState('');
  const [error, setError] = useState('');
  const nameRef = useRef(null);
  const otherRef = useRef(null);
  const slots = picker?.slots || [];
  const property = picker?.property || {};
  const where = [property.address, property.city].filter(Boolean).join(', ');
  const agentLabel = agentFirstName || 'your agent';
  const isOther = slot === 'other' || slots.length === 0;

  const pickSlot = (value) => {
    setSlot(value);
    setError('');
    setTimeout(() => (value === 'other' ? otherRef : nameRef).current?.focus(), 0);
  };

  const submit = (e) => {
    e.preventDefault();
    const when = isOther ? otherTimeText(otherDate, otherPart, otherNote) : slot?.label;
    if (!when) return setError(isOther ? 'Please pick a date that suits you.' : 'Please pick a time.');
    if (name.trim().length < 2) return setError('Please type your name.');
    if (method === 'phone' && !PHONE_DIGITS_OK(contact)) return setError('Please type a 10-digit phone number.');
    if (method === 'email' && !EMAIL_OK(contact)) return setError('Please type an email like name@example.com.');
    setError('');
    const timePart = isOther ? ` at a different time: ${when}` : ` on ${when}`;
    const how = method === 'phone' ? `phone: ${contact.trim()}` : `email: ${contact.trim()}`;
    onSubmit?.(`I'd like to book a showing for ${where}${timePart}. My name is ${name.trim()}. Please reach me by ${how}.`);
  };

  return (
    <form className="buyer-booking buyer-booking--picker" onSubmit={submit} aria-label={`Book a showing for ${where}`}>
      {slots.length > 0 && (
        <fieldset className="buyer-booking__group">
          <legend className="buyer-booking__label">Pick a time. {agentLabel} confirms every showing.</legend>
          <div className="buyer-booking__chips">
            {slots.map((s) => (
              <button
                key={`${s.date}-${s.time}`}
                type="button"
                className={`buyer-booking__chip${slot === s ? ' buyer-booking__chip--on' : ''}`}
                aria-pressed={slot === s}
                onClick={() => pickSlot(s)}
                disabled={disabled}
              >
                {s.label}
              </button>
            ))}
            <button
              type="button"
              className={`buyer-booking__chip${slot === 'other' ? ' buyer-booking__chip--on' : ''}`}
              aria-pressed={slot === 'other'}
              onClick={() => pickSlot('other')}
              disabled={disabled}
            >
              Another time
            </button>
          </div>
        </fieldset>
      )}
      {isOther && (
        <>
          <label className="buyer-booking__field">
            <span>Date you'd like</span>
            <input ref={otherRef} type="date" min={localIsoDate()} value={otherDate} onChange={(e) => setOtherDate(e.target.value)} disabled={disabled} />
          </label>
          <fieldset className="buyer-booking__group">
            <legend className="buyer-booking__label">Time of day</legend>
            <div className="buyer-booking__chips">
              {['Morning', 'Afternoon', 'Evening'].map((part) => (
                <button
                  key={part}
                  type="button"
                  className={`buyer-booking__chip${otherPart === part ? ' buyer-booking__chip--on' : ''}`}
                  aria-pressed={otherPart === part}
                  onClick={() => setOtherPart(otherPart === part ? null : part)}
                  disabled={disabled}
                >
                  {part}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="buyer-booking__field">
            <span>Anything else about timing (optional)</span>
            <input type="text" value={otherNote} onChange={(e) => setOtherNote(e.target.value)} disabled={disabled} />
          </label>
        </>
      )}
      <label className="buyer-booking__field">
        <span>Your name</span>
        <input ref={nameRef} type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} disabled={disabled} />
      </label>
      <fieldset className="buyer-booking__group">
        <legend className="buyer-booking__label">How should {agentLabel} reach you?</legend>
        <div className="buyer-booking__methods">
          <label><input type="radio" name="booking-method" value="phone" checked={method === 'phone'} onChange={() => { setMethod('phone'); setContact(''); }} disabled={disabled} /> Phone</label>
          <label><input type="radio" name="booking-method" value="email" checked={method === 'email'} onChange={() => { setMethod('email'); setContact(''); }} disabled={disabled} /> Email</label>
        </div>
      </fieldset>
      <label className="buyer-booking__field">
        <span>{method === 'phone' ? 'Phone number' : 'Email'}</span>
        <input
          type={method === 'phone' ? 'tel' : 'email'}
          autoComplete={method === 'phone' ? 'tel' : 'email'}
          inputMode={method === 'phone' ? 'tel' : 'email'}
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          disabled={disabled}
        />
      </label>
      {error && <p className="buyer-booking__error" role="alert">{error}</p>}
      <button type="submit" className="buyer-booking__submit" disabled={disabled}>Send showing request</button>
    </form>
  );
}

/**
 * Card description: a sentence or two, then "Read more" opens the full
 * listing text and the extra listing facts in the card itself, no chat turn
 * needed (Lillian, 2026-10-25).
 */
function CardDescription({ p }) {
  const [open, setOpen] = useState(false);
  const full = String(p.public_remarks || '').replace(/\s+/g, ' ').trim();
  const short = shortRemarks(full);
  const extras = [
    p.heating && ['Heating', p.heating],
    p.cooling && ['Cooling', p.cooling],
    p.basement && ['Basement', p.basement],
  ].filter(Boolean);
  const hasMore = short !== full || extras.length > 0;
  if (!full && !extras.length) return null;
  return (
    <div className="buyer-listing-card__desc-wrap">
      {full && <p className="buyer-listing-card__desc">{open ? full : short}</p>}
      {open && extras.length > 0 && (
        <ul className="buyer-listing-card__facts">
          {extras.map(([label, value]) => (
            <li key={label}><span>{label}</span> {value}</li>
          ))}
        </ul>
      )}
      {hasMore && (
        <button
          type="button"
          className="buyer-listing-card__readmore"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          {open ? 'Show less ▴' : 'Read more ▾'}
        </button>
      )}
    </div>
  );
}

function homeKey(p) {
  return String(p?.mls_number || p?.id || p?.address || '').trim().toUpperCase();
}

/** Every home shown in the chat so far, first-shown order, one each. */
function homesShownInChat(messages) {
  const seen = new Set();
  const homes = [];
  for (const m of messages || []) {
    if (m?.role !== 'assistant') continue;
    for (const p of m.featured_properties || []) {
      const k = homeKey(p);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      homes.push(p);
    }
  }
  return homes;
}

/**
 * Replies whose one card is a home already carded earlier: the reply above it
 * has the facts, so the card shrinks to photos and Book a showing
 * (Lillian, 2026-10-26).
 */
function repeatCardMessageIds(messages) {
  const seen = new Set();
  const ids = new Set();
  for (const m of messages || []) {
    if (m?.role !== 'assistant') continue;
    const cards = m.featured_properties || [];
    if (cards.length === 1 && seen.has(homeKey(cards[0]))) ids.add(m.id);
    for (const p of cards) seen.add(homeKey(p));
  }
  return ids;
}

/**
 * Slim row above the composer so a buyer can get back to any home without
 * scrolling up through the thread (Lillian, 2026-10-25).
 */
function HomesInChatBar({ homes, disabled, onTellMore, onBook }) {
  const [openKey, setOpenKey] = useState(null);
  if (!homes.length) return null;
  const open = homes.find((p) => homeKey(p) === openKey) || null;
  const jumpTo = (p) => {
    const cards = document.querySelectorAll(`[data-home-key="${CSS.escape(homeKey(p))}"]`);
    const card = cards[cards.length - 1];
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card.classList.add('buyer-listing-card--flash');
      setTimeout(() => card.classList.remove('buyer-listing-card--flash'), 1600);
    }
    setOpenKey(null);
  };
  return (
    <div className="buyer-homes-bar" aria-label="Homes in this chat">
      <div className="buyer-homes-bar__label">Homes in this chat ({homes.length})</div>
      <div className="buyer-homes-bar__row">
        {homes.map((p) => {
          const k = homeKey(p);
          const street = String(p.address || '').split(',')[0];
          return (
            <button
              key={k}
              type="button"
              className={`buyer-homes-bar__chip${openKey === k ? ' is-open' : ''}`}
              onClick={() => setOpenKey(openKey === k ? null : k)}
              aria-expanded={openKey === k}
            >
              {cardPhotoUrl(p)
                ? <img src={cardPhotoUrl(p)} alt="" className="buyer-homes-bar__thumb" loading="lazy" />
                : <span className="buyer-homes-bar__thumb buyer-homes-bar__thumb--empty" />}
              <span className="buyer-homes-bar__text">
                <span className="buyer-homes-bar__street">{street}</span>
                {p.list_price != null && (
                  <span className="buyer-homes-bar__price">${Number(p.list_price).toLocaleString()}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      {open && (
        <div className="buyer-homes-bar__actions">
          <span className="buyer-homes-bar__open-name">
            {String(open.address || '').split(',')[0]}{open.city ? `, ${open.city}` : ''}
          </span>
          <button type="button" className="buyer-listing-card__more" disabled={disabled}
            onClick={() => { setOpenKey(null); onTellMore(open); }}>
            Tell me more
          </button>
          <button type="button" className="buyer-listing-card__book" disabled={disabled}
            onClick={() => { setOpenKey(null); onBook(open); }}>
            <Calendar size={14} /> Book a showing
          </button>
          <button type="button" className="buyer-listing-card__pass" onClick={() => jumpTo(open)}>
            Jump to card
          </button>
        </div>
      )}
    </div>
  );
}

export function PropertyPhotoCards({
  properties,
  canAct,
  states,
  bookedKeys,
  onAction,
  onBook,
  onTellMore,
  variant = 'full',
  onSelect,
  agentFirstName,
  disabled = false,
}) {
  if (!properties?.length) return null;
  const compact = variant === 'compact';
  // "X's listing" is only useful signal when the set actually mixes the
  // agent's own listings with outside DDF inventory — on an all-own set it's
  // noise repeated on every card.
  const hasMixedInventory = properties.some((p) => p.is_own_listing)
    && properties.some((p) => !p.is_own_listing);

  if (compact) {
    return (
      <div className="buyer-listing-cards buyer-listing-cards--compact">
        {properties.map((p) => {
          const where = [p.address, p.city].filter(Boolean).join(', ');
          return (
            <button
              key={p.id || p.mls_number}
              type="button"
              className="buyer-listing-card buyer-listing-card--compact"
              onClick={() => onSelect?.(p)}
              disabled={disabled}
            >
              <div className="buyer-listing-card__media">
                {cardPhotoUrl(p) ? (
                  <img
                    className="buyer-listing-card__photo"
                    src={cardPhotoUrl(p)}
                    alt=""
                    loading="lazy"
                  />
                ) : (
                  <div className="buyer-listing-card__photo buyer-listing-card__photo--placeholder">
                    Photo soon
                  </div>
                )}
                {p.list_price != null && (
                  <span className="buyer-listing-card__price">
                    ${Number(p.list_price).toLocaleString()}
                  </span>
                )}
                {hasMixedInventory && p.is_own_listing && (
                  <span className="buyer-listing-card__own-badge">
                    {agentFirstName || 'Agent'}&apos;s listing
                  </span>
                )}
              </div>
              <div className="buyer-listing-card__body">
                <div className="buyer-listing-card__address">{where}</div>
                <div className="buyer-listing-card__meta">
                  {p.bedrooms != null && <span>{p.bedrooms} bed</span>}
                  {p.bathrooms != null && <span>{Number.isInteger(Number(p.bathrooms)) ? Number(p.bathrooms) : p.bathrooms} bath</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  const single = properties.length === 1;
  const detail = single && variant === 'detail';
  return (
    <div className={`buyer-listing-cards${single ? ' buyer-listing-cards--single' : ''}`}>
      {properties.map((p) => {
        const stateKey = p.mls_number || p.id;
        const cardState = states?.[stateKey]?.state || null;
        // A requested showing supersedes save/pass — the card locks into a
        // clean "showing requested" view with no further actions.
        const isBooked = Boolean(
          (p.mls_number && bookedKeys?.has(String(p.mls_number).toUpperCase()))
          || (p.address && bookedKeys?.has(String(p.address).trim().toUpperCase()))
        );
        const isPassed = !isBooked && cardState === 'passed';
        const isSaved = cardState === 'saved';
        return (
        <article
          key={p.id || p.mls_number}
          data-home-key={homeKey(p)}
          className={`buyer-listing-card${isPassed ? ' buyer-listing-card--passed' : ''}`}
        >
          <CardMedia p={p} thumbs={single}>
            {p.list_price != null && (
              <span className="buyer-listing-card__price">
                ${Number(p.list_price).toLocaleString()}
              </span>
            )}
            {canAct && !isBooked && (
              <button
                type="button"
                className={`buyer-listing-card__heart${isSaved ? ' is-active' : ''}`}
                onClick={() => onAction(p, isSaved ? 'clear' : 'saved')}
                aria-pressed={isSaved}
                aria-label={isSaved ? 'Remove from your saved homes' : 'Save this home'}
                title={isSaved ? 'Saved' : 'Save this home'}
              >
                <Heart size={18} fill={isSaved ? 'currentColor' : 'none'} />
              </button>
            )}
          </CardMedia>
          <div className="buyer-listing-card__body">
            {isPassed && (
              <span className="buyer-listing-card__passed-badge">Not for me</span>
            )}
            <div className="buyer-listing-card__address">
              {p.address}{p.city ? `, ${p.city}` : ''}
            </div>
            {!detail && <div className="buyer-listing-card__meta">
              {p.bedrooms != null && <span>{p.bedrooms} bed</span>}
              {p.bathrooms != null && <span>{Number.isInteger(Number(p.bathrooms)) ? Number(p.bathrooms) : p.bathrooms} bath</span>}
              {p.square_feet != null && (
                <span>{Number(p.square_feet).toLocaleString()} sq ft</span>
              )}
              {p.property_type && (
                <span>{formatPropertyTypeLabel(p.property_type)}</span>
              )}
              {p.year_built ? <span>Built {p.year_built}</span> : null}
              {p.lot_size_sqft != null && (
                <span>{Number(p.lot_size_sqft).toLocaleString()} sq ft lot</span>
              )}
              {p.garage
                ? <span>Garage{p.garage_spaces ? ` (${p.garage_spaces})` : ''}</span>
                : (p.parking_features
                  ? <span>{p.parking_features}</span>
                  : (p.parking_total != null ? <span>{p.parking_total} parking</span> : null))}
              {p.tax_annual_amount != null && (
                <span>
                  Taxes ${Number(p.tax_annual_amount).toLocaleString()}
                  {p.tax_year ? ` (${p.tax_year})` : ''}
                </span>
              )}
            </div>}
            {!detail && <CardDescription p={p} />}
            {!detail && p.owner_story && (
              <p className="buyer-listing-card__owner">
                <span className="buyer-listing-card__owner-label">From the owner</span>
                {p.owner_story}
              </p>
            )}
            {(p.mls_number || p.list_brokerage || p.is_own_listing) && (
              <div className="buyer-listing-card__mls">
                {p.mls_number ? <>MLS® {p.mls_number}</> : null}
                {(p.mls_number && (p.is_own_listing || p.list_brokerage)) ? ' · ' : ''}
                {p.is_own_listing
                  ? `Listed by ${p.listing_agent_name || agentFirstName || 'your agent'}${
                      p.list_brokerage ? ` · ${p.list_brokerage}` : ''
                    }`
                  : (p.list_brokerage ? `Listing courtesy of ${p.list_brokerage}` : '')}
              </div>
            )}
            {listingMapsHref(p) && (
              <a
                className="buyer-listing-card__maps"
                href={listingMapsHref(p)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MapPin size={13} /> Open in Maps
              </a>
            )}
            {canAct && isBooked && (
              <div className="buyer-listing-card__booked" role="status">
                <Calendar size={14} /> Showing requested — your agent will confirm
              </div>
            )}
            {canAct && !isBooked && (
              <div className="buyer-listing-card__actions">
                <button
                  type="button"
                  className="buyer-listing-card__book"
                  onClick={() => onBook?.(p)}
                >
                  <Calendar size={14} /> Book a showing
                </button>
                {onTellMore && !detail && (
                  <button
                    type="button"
                    className="buyer-listing-card__more"
                    onClick={() => onTellMore(p)}
                  >
                    Tell me more
                  </button>
                )}
                {!isPassed && !detail && (
                  <button
                    type="button"
                    className="buyer-listing-card__pass"
                    onClick={() => onAction(p, 'passed')}
                  >
                    Not for me
                  </button>
                )}
              </div>
            )}
          </div>
        </article>
        );
      })}
    </div>
  );
}

// Buyer-facing recap of the locally captured draft criteria (wizard shape:
// areas / must_haves are plain strings, not arrays).
function formatDraftCriteriaLine(draft) {
  if (!draft || typeof draft !== 'object') return null;
  const parts = [];
  if (draft.price_max) {
    const min = draft.price_min;
    parts.push(
      min
        ? `$${Number(min).toLocaleString()}–$${Number(draft.price_max).toLocaleString()}`
        : `${draft.price_around ? 'around' : 'under'} $${Number(draft.price_max).toLocaleString()}`
    );
  } else if (draft.price_min) {
    parts.push(`from $${Number(draft.price_min).toLocaleString()}`);
  }
  if (draft.bedrooms_min) parts.push(`${draft.bedrooms_min}+ beds`);
  if (draft.bathrooms_min) parts.push(`${draft.bathrooms_min}+ baths`);
  if (draft.property_type) parts.push(String(draft.property_type));
  else if (Array.isArray(draft.property_types) && draft.property_types.length) {
    parts.push(draft.property_types.map((t) => String(t).replace(/_/g, ' ')).join(' or '));
  }
  if (draft.areas?.length) {
    parts.push(Array.isArray(draft.areas) ? draft.areas.join(', ') : String(draft.areas));
  }
  if (draft.must_haves_text) parts.push(String(draft.must_haves_text));
  else if (draft.must_haves?.length) {
    parts.push(Array.isArray(draft.must_haves) ? draft.must_haves.join(', ') : String(draft.must_haves));
  }
  return parts.length ? parts.join(' · ') : null;
}

/** True when budget, beds, or areas have been captured (matches backend hasCoreCriteria). */
function hasCoreSearchCriteria(criteria) {
  if (!criteria || typeof criteria !== 'object') return false;
  const areas = criteria.areas;
  const hasAreas = Array.isArray(areas)
    ? areas.length > 0
    : Boolean(areas && String(areas).trim());
  return criteria.price_min != null
    || criteria.price_max != null
    || criteria.budget_min != null
    || criteria.budget_max != null
    || criteria.bedrooms_min != null
    || criteria.bedrooms != null
    || hasAreas;
}

/** getUserMedia failures all look the same by default — split out the common,
 * distinguishable causes so "no mic" and "mic in use" don't read as a permissions issue. */
function describeMicError(err, context = 'to use voice') {
  const name = err?.name;
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No microphone found. Check that one is connected and try again.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'Your microphone seems to be in use by another app. Close it and try again.';
  }
  return `Microphone access is needed ${context}. Check your browser permissions.`;
}

/** Turns a completed BuyerCriteriaWizard payload into the visible opening chat message. */
function summarizeWizardCriteriaForChat(payload) {
  const parts = [];
  if (payload.price_max) {
    parts.push(
      payload.price_min
        ? `$${Number(payload.price_min).toLocaleString()}–$${Number(payload.price_max).toLocaleString()}`
        : `up to $${Number(payload.price_max).toLocaleString()}`
    );
  } else if (payload.price_min) {
    parts.push(`from $${Number(payload.price_min).toLocaleString()}`);
  }
  if (payload.bedrooms_min) parts.push(`${payload.bedrooms_min}+ bedrooms`);
  if (payload.bathrooms_min) parts.push(`${payload.bathrooms_min}+ bathrooms`);
  if (payload.areas) parts.push(`in ${payload.areas}`);
  if (payload.timeline) parts.push(`timeline: ${payload.timeline}`);
  if (payload.pre_approved) parts.push(`pre-approval: ${payload.pre_approved}`);
  if (payload.must_haves_text) parts.push(`must-haves: ${payload.must_haves_text}`);
  return parts.length
    ? `Here's what I'm looking for: ${parts.join(', ')}.`
    : "I'd like to start looking for a home.";
}

// Save-search register card — Phase 1a (focus-group mode).
//
// IMPORTANT: This is a DEMO-SAFE experience. It renders and responds fully but
// persists NOTHING and sends NOTHING (no account, no confirmation email, no
// alerts). Real persistence + double opt-in is Phase 1b, gated on PIPEDA legal
// review. Per the honesty gate, copy must NOT promise alerts until CREA DDF is
// live. Consent boxes are per-channel and DEFAULT UNCHECKED (CASL).
// Save-search card — routes into the EXISTING client registration flow
// (/client/:slug/auth), which already links the current conversation and draft
// criteria from localStorage on register. No new capture plumbing here, and no
// alert-promise copy (honesty gate: alerts stay unpromised until CREA DDF is
// live; consent capture happens on the registration page itself).
function clientAuthPath(agentSlug, { mode, role, conversationId } = {}) {
  const qs = new URLSearchParams();
  if (mode) qs.set('mode', mode);
  if (role) qs.set('role', role);
  if (conversationId) qs.set('conversation', conversationId);
  const q = qs.toString();
  return `/client/${agentSlug}/auth${q ? `?${q}` : ''}`;
}

function SaveSearchCard({ aiName, agentFirstName, agentSlug, conversationId, recap, onDismiss }) {
  const authHref = clientAuthPath(agentSlug, { mode: 'register', conversationId });

  return (
    <div className="buyer-savecard" role="region" aria-label="Save your search">
      <div className="buyer-savecard__head">
        <span className="buyer-savecard__icon" aria-hidden><Bookmark size={18} /></span>
        <div>
          <div className="buyer-savecard__title">Save your search</div>
          <div className="buyer-savecard__sub">Create a free account — pick up right where you left off</div>
        </div>
      </div>

      {recap && (
        <div className="buyer-savecard__crit">
          <MapPin size={15} className="buyer-savecard__crit-pin" aria-hidden />
          <span>{recap}</span>
        </div>
      )}

      <div className="buyer-savecard__body">
        <ul className="buyer-savecard__benefits">
          <li>{aiName} keeps your budget, areas, and preferences on file</li>
          <li>Continue this chat anytime, from any device</li>
          <li>{agentFirstName} sees what you&apos;re looking for and can follow up with real context</li>
        </ul>

        <Link to={authHref} className="buyer-savecard__btn">
          Save my search — free account
        </Link>

        <p className="buyer-savecard__fine">
          Free and no obligation — this chat will be linked to your account.
          You&apos;ll confirm your details and consent on the next screen.
        </p>
        <button type="button" className="buyer-savecard__notnow" onClick={onDismiss}>Not now</button>
      </div>
    </div>
  );
}

// Seller "Your home" profile card — amber-accented, shown at the capture/handoff
// moment so the seller sees their info captured. Honest: no valuation is quoted;
// the agent prepares the CMA. Surfaces address + timeline (highest-value signal).
function HomeProfileCard({ data, agentFirstName }) {
  if (!data) return null;
  const addr = [data.property_address, data.property_city].filter(Boolean).join(', ');
  const details = [
    data.property_type ? formatPropertyTypeLabel(data.property_type) : null,
    data.bedrooms != null && data.bedrooms !== '' ? `${data.bedrooms} bed` : null,
    data.bathrooms != null && data.bathrooms !== '' ? `${data.bathrooms} bath` : null,
    data.approx_age ? `Built ${data.approx_age}` : null,
  ].filter(Boolean);
  const agent = agentFirstName || 'Your agent';
  return (
    <div className="buyer-home-card">
      <div className="buyer-home-card__head"><Home size={14} /> Your home</div>
      {addr && <div className="buyer-home-card__addr">{addr}</div>}
      {details.length > 0 && (
        <div className="buyer-home-card__meta">
          {details.map((d, i) => <span key={i}>{d}</span>)}
        </div>
      )}
      {data.condition && <div className="buyer-home-card__row">Condition: {data.condition}</div>}
      {data.features && <div className="buyer-home-card__row">Features: {data.features}</div>}
      {data.timeline && (
        <div className="buyer-home-card__timeline">
          <span className="buyer-home-card__timeline-label">Timeline</span>
          {data.timeline}
        </div>
      )}
      <p className="buyer-home-card__note">
        {agent} will prepare a comparative market analysis from recent sales — yours to keep, no
        obligation, even if you decide not to list. {agent} will text to confirm.
      </p>
    </div>
  );
}

function PropertyPhotoGalleries({ galleries }) {
  if (!galleries?.length) return null;
  const getPlaceholderCount = (gallery) => {
    const n = Number(gallery?.placeholder_count);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 4;
  };
  return (
    <div className="buyer-photo-galleries">
      {galleries.map((g) => (
        <section key={g.id || g.mls_number} className="buyer-photo-gallery">
          <div className="buyer-photo-gallery__header">
            <span className="buyer-photo-gallery__title">
              {g.address}{g.city ? `, ${g.city}` : ''}
            </span>
            {g.mls_number && (
              <span className="buyer-photo-gallery__mls">MLS® {g.mls_number}</span>
            )}
          </div>
          <div className="buyer-photo-gallery__grid">
            {g.photos?.length ? (
              g.photos.map((ph, i) => (
                <figure key={`${g.mls_number}-${i}`} className="buyer-photo-gallery__item">
                  <img
                    src={ph.url}
                    alt={ph.caption || `${g.address} photo ${i + 1}`}
                    loading="lazy"
                  />
                  {ph.caption && (
                    <figcaption className="buyer-photo-gallery__caption">{ph.caption}</figcaption>
                  )}
                </figure>
              ))
            ) : (
              Array.from({ length: getPlaceholderCount(g) }).map((_, i) => (
                <div key={`${g.mls_number}-ph-${i}`} className="buyer-photo-gallery__placeholder">
                  <span>Photo placeholder</span>
                </div>
              ))
            )}
          </div>
          {!g.photos?.length && (
            <div className="buyer-photo-gallery__note">
              Demo mode: gallery placeholders shown until listing photos are available.
            </div>
          )}
          {g.map_link && (
            <a
              className="buyer-photo-gallery__map"
              href={g.map_link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MapPin size={13} /> Open in Maps
            </a>
          )}
        </section>
      ))}
    </div>
  );
}

function MessageContentInner({ content, featuredProperties, cardVariant, propertyGalleries, canAct, propertyStates, bookedKeys, onPropertyAction, onPropertyBook, onPropertyTellMore, onOptionSelect }) {
  let blocks = formatMessage(content);
  if (featuredProperties?.length) {
    blocks = blocks.filter((block) => block.type !== 'property');
  }

  return (
    <div className="buyer-msg__stack">
      {blocks.map((block, i) => {
        if (block.type === 'fact') {
          return (
            <div key={i} className="buyer-msg__fact">
              <span className="buyer-msg__fact-mark" aria-hidden="true">•</span>
              <span className="buyer-msg__fact-text">{block.text}</span>
            </div>
          );
        }
        if (block.type === 'owner') {
          return (
            <div key={i} className="buyer-msg__owner-note">
              <span className="buyer-msg__owner-note-label">From the owner</span>
              <span className="buyer-msg__owner-note-text">{block.text}</span>
            </div>
          );
        }
        if (block.type === 'item') {
          const numberMatch = block.line.match(/^\d+/);
          const itemText = block.line.replace(/^\d+[.\-)]\s*/, '').replace(/^[-•]\s*/, '');
          // Numbered items in the newest reply are usually options Ava is
          // offering — make them tappable so the user doesn't have to type.
          if (numberMatch && onOptionSelect) {
            return (
              <button
                key={i}
                type="button"
                className="buyer-msg__option"
                onClick={() => onOptionSelect(itemText)}
              >
                <span className="buyer-msg__option-num">{numberMatch[0]}.</span>
                <span className="buyer-msg__option-text">{itemText}</span>
              </button>
            );
          }
          return (
            <div key={i} className="buyer-msg__item">
              <span className="buyer-msg__item-num">
                {numberMatch ? numberMatch[0] + '.' : '•'}
              </span>
              <span className="buyer-msg__item-text">{itemText}</span>
            </div>
          );
        }
        if (block.type === 'property') {
          const [address, priceInfo, ...rest] = block.lines;
          const description = rest.join(' ');
          return (
            <div key={i} className="buyer-msg__property">
              <div className="buyer-msg__property-address">{address}</div>
              {priceInfo && (
                <div className="buyer-msg__property-meta">{priceInfo}</div>
              )}
              {description && (
                <div className="buyer-msg__property-desc">{description}</div>
              )}
            </div>
          );
        }
        if (block.type === 'area_section') {
          return (
            <section key={i} className="buyer-msg__area">
              <div className="buyer-msg__area-title">{block.area}</div>
              <p className="buyer-msg__area-body">{block.description}</p>
            </section>
          );
        }
        return (
          <div key={i} className="buyer-msg__prose">
            {block.lines.map((line, j) => (
              <p key={j}>{line}</p>
            ))}
          </div>
        );
      })}
      <PropertyPhotoCards
        properties={featuredProperties}
        variant={cardVariant}
        canAct={canAct}
        states={propertyStates}
        bookedKeys={bookedKeys}
        onAction={onPropertyAction}
        onBook={onPropertyBook}
        onTellMore={onPropertyTellMore}
      />
      <PropertyPhotoGalleries galleries={propertyGalleries} />
    </div>
  );
}

// Composer keystrokes re-render the whole thread; without memo every already-
// rendered message re-parses its markdown on every one of those renders.
const MessageContent = memo(MessageContentInner);

// Module-level so the loop survives the landing → conversation remount.
// A chip click starts it; the new page stops it when Ava's reply arrives.
let sharedThinkingAmbience = null;

function startSharedThinkingAmbience() {
  try {
    if (!sharedThinkingAmbience) {
      const audio = new Audio('/thinking-ambience.wav');
      audio.loop = true;
      audio.volume = 0.45;
      sharedThinkingAmbience = audio;
    }
    const audio = sharedThinkingAmbience;
    if (audio.paused || audio.ended) {
      audio.currentTime = 0;
      audio.play().catch((e) => {
        console.warn('[Voice] Thinking ambience playback failed (non-fatal):', e?.message || e);
      });
    }
  } catch (e) {
    console.warn('[Voice] Thinking ambience failed to start (non-fatal):', e?.message || e);
  }
}

function stopSharedThinkingAmbience() {
  const audio = sharedThinkingAmbience;
  if (!audio) return;
  try {
    audio.pause();
    audio.currentTime = 0;
  } catch (_) { /* ignore */ }
  sharedThinkingAmbience = null;
}

export default function BuyerChat() {
  const { slug, conversationId: routeConversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [testerMode, setTesterMode] = useState(() => syncTesterModeFromSearch(
    typeof window !== 'undefined' ? window.location.search : ''
  ));
  const isConversationView = Boolean(routeConversationId);
  const { client, loading: clientAuthLoading, reload: reloadClient } = useClientAuth();

  const [agent, setAgent] = useState(null);
  const [agentLoading, setAgentLoading] = useState(true);
  const [agentError, setAgentError] = useState(null);
  // Read before paint so a saved day choice is not stuck on the evening class.
  const [daylight, setDaylight] = useState(readBuyerDaylight);
  function toggleDaylight() {
    setDaylight((on) => {
      const next = !on;
      writeBuyerDaylight(next);
      return next;
    });
  }

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState(null);
  // First-message PIPEDA notice. Unknown until a conversation transcript loads;
  // landing starts as needed so a shared-device "new conversation" always sees it.
  const [pipedaAcked, setPipedaAcked] = useState(false);
  const pipedaAckedRef = useRef(false);
  // New conversation keeps the consent already given in this visit.
  const carryConsentRef = useRef(false);
  const pendingSellerStartRef = useRef(false);
  const [sellerAwaitingConsent, setSellerAwaitingConsent] = useState(false);
  const [pipedaKnown, setPipedaKnown] = useState(() => !Boolean(routeConversationId));
  const [started, setStarted] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [voiceError, setVoiceError] = useState(null);
  // Soft notice when TTS isn't configured (not an error-toned banner).
  const [voiceNotice, setVoiceNotice] = useState(null);
  // Hands-free conversation mode: open mic, silence detection auto-sends,
  // the mic reopens after Ava finishes speaking — until the user taps stop.
  const [handsFree, setHandsFree] = useState(false);
  // A hands-free turn produced no usable transcript — tell the user to retry
  // instead of silently reopening the mic (which reads as a hang).
  const [hfMissNotice, setHfMissNotice] = useState(false);
  // Live captions during a hands-free turn are shown in the status bar, NOT the
  // composer input — the input belongs to the user's typed text at all times.
  const [hfCaption, setHfCaption] = useState('');
  const [inputHighlight, setInputHighlight] = useState(false);
  const [resumingChat, setResumingChat] = useState(false);
  const [draftCriteria, setDraftCriteria] = useState(null);
  // Header "Save search" — only once budget/areas/beds exist for this chat.
  const [hasSearchCriteria, setHasSearchCriteria] = useState(false);
  // Accumulates CRITERIA_SNAPSHOT fields turn-by-turn for the live "captured
  // so far" strip — later turns' fields overlay earlier ones, nothing is lost.
  const [liveCriteria, setLiveCriteria] = useState(null);
  // Optional fast-path form instead of talking through criteria — new-user landing only.
  const [showCriteriaWizard, setShowCriteriaWizard] = useState(false);
  const wizardStartMessageRef = useRef(null);
  const [savePromptDismissed, setSavePromptDismissed] = useState(false);
  const [saveCardAutoShown, setSaveCardAutoShown] = useState(false);
  const [saveCardStrongReofferUsed, setSaveCardStrongReofferUsed] = useState(false);
  /** Favourite / booking that happened after "Not now" — one re-offer allowed. */
  const [postDismissSaveSignal, setPostDismissSaveSignal] = useState(false);
  /** Pin card under a specific assistant message so latching autoShown doesn't hide it. */
  const [saveCardAnchorId, setSaveCardAnchorId] = useState(null);
  const [sellerConsultComplete, setSellerConsultComplete] = useState(false);
  const [chatBranch, setChatBranch] = useState(null); // 'buyer' | 'seller' once known
  // Which localStorage conversation pointer the active UI owns.
  const pathModeRef = useRef('buyer'); // 'buyer' | 'seller'
  // Area picker: which region's community chips are expanded (null = tier 1).
  // Landing mode-picker: choose voice/text, then go straight into an open conversation.
  const [chatMode, setChatMode] = useState(null); // 'voice' | 'text' once started
  const [chatVisitEnding, setChatVisitEnding] = useState(false);
  const [showLandingLegal, setShowLandingLegal] = useState(false);
  const [showConversationLegal, setShowConversationLegal] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const legalCreaRef = useRef(null);
  const hydrateDoneRef = useRef(null);
  // Whether auto-scroll follow is on. Re-enabled on each user send; suspended
  // when the buyer scrolls manually (e.g. while Ava is thinking / after a tall reply).
  const stickToBottomRef = useRef(true);
  const programmaticScrollRef = useRef(false);
  const lastAutoScrollMsgIdRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const playbackAudioRef = useRef(null);
  const playbackCtxRef = useRef(null);
  const playbackSourceRef = useRef(null);
  const playbackUrlRef = useRef(null);
  const cancelRecordingRef = useRef(false);
  const pendingStopRef = useRef(false);
  const homeKeyDownRef = useRef(false);
  const fillerPlaybackPromiseRef = useRef(Promise.resolve());
  const lastFillerPhraseRef = useRef('');
  const recordMimeRef = useRef('audio/webm');
  const uploadExtRef = useRef('webm');
  const speechRecognitionRef = useRef(null);
  const liveTranscriptRef = useRef('');
  const recognitionEndResolveRef = useRef(null);
  const resumeDoneRef = useRef(false);
  const resumeInFlightRef = useRef(false);
  const autoFreshStartRef = useRef(false);
  /** Portal "Ask Ava" deep link — consume once so refresh doesn't re-fire. */
  const askPropertyConsumedRef = useRef(false);
  const userInitiatedNewChatRef = useRef(false);
  const localChatActivityRef = useRef(false);
  const convIdRef = useRef(null);
  const startedRef = useRef(false);
  const chatModeRef = useRef(null);
  // Show the "voice replies unavailable" soft notice at most once per page session.
  const ttsUnavailableNotifiedRef = useRef(false);
  // Hands-free mode refs — kept separate from the hold-to-talk recorder so the
  // two capture paths can't interfere with each other.
  const handsFreeRef = useRef(false);
  const hfStreamRef = useRef(null);
  const hfRecorderRef = useRef(null);
  const hfChunksRef = useRef([]);
  const hfAudioCtxRef = useRef(null);
  const hfMonitorTimerRef = useRef(null);
  const hfSegmentActiveRef = useRef(false);
  const hfFinishRequestedRef = useRef(false);
  const startHandsFreeSegmentRef = useRef(null);
  const cancelHandsFreeSegmentRef = useRef(null);
  const earconCtxRef = useRef(null);

  // Barge-in monitor: records continuously from the moment Ava starts
  // speaking (so nothing said during the interruption-detection window is
  // ever lost), using its own refs — never reaches into hfSegmentActiveRef/
  // hfRecorderRef/hfStreamRef directly. On a confirmed interruption it hands
  // the already-running stream/recorder/chunks to startHandsFreeSegment
  // through its opts, the same entry point the normal reopen loop already
  // uses safely — it never mutates the hf* machinery itself.
  const bargeInStreamRef = useRef(null);
  const bargeInAudioCtxRef = useRef(null);
  const bargeInTimerRef = useRef(null);
  const bargeInRecorderRef = useRef(null);
  const bargeInChunksRef = useRef([]);
  const bargeInHandedOffRef = useRef(false);
  // Ava's reply paused (not stopped) by a barge-in, so a mug set down on the
  // table can't cut her off for good (Lillian's Just talk test, 2026-10-26).
  const bargeInPausedRef = useRef(null);

  /** Conversation is in voice mode if the Voice/hands-free session is active. */
  const isConversationVoiceMode = useCallback(() => (
    chatModeRef.current === 'voice' || handsFreeRef.current === true
  ), []);

  /**
   * Central voice-turn flag for API + TTS. UI actions (heart, chips, book)
   * must not pass fromVoice — they inherit from conversation voice mode.
   * Mic turns also promote the session to voice so later UI actions speak.
   */
  const resolveVoiceTurn = useCallback((explicitFromVoice = false) => {
    if (explicitFromVoice) {
      if (chatModeRef.current !== 'voice') {
        chatModeRef.current = 'voice';
        setChatMode('voice');
      }
      return true;
    }
    return isConversationVoiceMode();
  }, [isConversationVoiceMode]);

  useEffect(() => {
    const targetSlug = (slug ? String(slug).trim() : 'sarah-murphy').toLowerCase();
    let cancelled = false;
    setAgentLoading(true);
    setAgentError(null);

    const applyAgentFromClient = () => {
      if (!client?.agent?.id) return false;
      const a = client.agent;
      const slugMatch = !a.slug || String(a.slug).toLowerCase() === targetSlug;
      if (!slugMatch) return false;
      setAgent({
        id: a.id,
        first_name: a.first_name,
        last_name: a.last_name,
        brokerage: a.brokerage,
        city: a.city,
        province: 'NL',
        slug: a.slug || targetSlug,
        assistant_name: a.assistant_name || 'Ava',
        voice_id: a.voice_id || '',
        avatar_filename: a.avatar_filename || 'ava-avatar.png',
      });
      setAgentError(null);
      return true;
    };

    publicApi.get(`/auth/agent/${encodeURIComponent(targetSlug)}`, {
      headers: { Authorization: '' },
    })
      .then((res) => {
        if (!cancelled) {
          setAgent(res.data.agent);
          setAgentError(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (applyAgentFromClient()) return;
        const status = err.response?.status;
        if (status === 404) {
          setAgentError('not_found');
        } else if (!err.response) {
          setAgentError('unreachable');
        } else {
          setAgentError('load_failed');
        }
      })
      .finally(() => {
        if (!cancelled) setAgentLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug, client?.agent]);

  const agentSlug = slug || agent?.slug;

  useEffect(() => {
    if (!agentSlug || client) return;
    try {
      const raw = localStorage.getItem(draftCriteriaStorageKey(agentSlug));
      if (raw) {
        const draft = JSON.parse(raw);
        setDraftCriteria(draft);
        if (hasCoreSearchCriteria(draft)) setHasSearchCriteria(true);
      }
    } catch { /* ignore */ }
    // Landing actions start a new thread. Only /conversation/:id resumes.
    setSavePromptDismissed(sessionStorage.getItem(savePromptDismissKey(agentSlug)) === '1');
    setSaveCardAutoShown(sessionStorage.getItem(saveCardAutoShownKey(agentSlug)) === '1');
    setSaveCardStrongReofferUsed(sessionStorage.getItem(saveCardStrongReofferKey(agentSlug)) === '1');
    setPostDismissSaveSignal(false);
    setSaveCardAnchorId(null);
  }, [agentSlug, client]);

  useEffect(() => {
    setTesterMode(syncTesterModeFromSearch(location.search));
  }, [location.search]);

  // Wizard writes draftCriteria via setState, then flags a pending start here —
  // waiting a render avoids sending the chat's first message with a stale
  // (pre-update) draftCriteria closure inside beginChat/persistDraftToConversation.
  useEffect(() => {
    if (!wizardStartMessageRef.current || started || !agent?.id) return;
    const text = wizardStartMessageRef.current;
    wizardStartMessageRef.current = null;
    submitUserTextRef.current?.(text, { pathIntent: 'buyer', carryDraft: true });
  }, [draftCriteria, started, agent?.id]);

  // Landing never shows an active thread — conversation lives on /conversation/:id
  useEffect(() => {
    if (isConversationView) return;
    setStarted(false);
    setMessages([]);
    setConvId(null);
    setChatVisitEnding(false);
    grantPipeda(carryConsentRef.current);
    carryConsentRef.current = false;
  }, [isConversationView, agentSlug]);

  // Hands-free is restored per conversation during hydrate — never from an
  // agent-scoped session flag (that leaked into New conversation / seller CTA).
  useEffect(() => {
    if (!agentSlug) return;
    clearLegacyHandsFreePreference(agentSlug);
  }, [agentSlug]);

  useEffect(() => {
    if (!agentSlug || !client) return;
    const token = localStorage.getItem(clientTokenKey(agentSlug));
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
  }, [client, agentSlug]);

  useEffect(() => {
    convIdRef.current = convId;
    if (convId && agentSlug) {
      setStoredConversationId(agentSlug, convId, pathModeRef.current);
    }
  }, [convId, agentSlug]);

  const grantPipeda = (granted) => {
    setPipedaAcked(granted);
    pipedaAckedRef.current = granted;
    setPipedaKnown(true);
  };

  const acknowledgePipeda = async () => {
    const startSeller = pendingSellerStartRef.current === true;
    pendingSellerStartRef.current = false;
    setSellerAwaitingConsent(false);
    grantPipeda(true);
    const id = convIdRef.current;
    if (id && agent?.id) {
      try {
        await publicApi.patch(
          `/conversations/public/${id}/consent`,
          { agent_id: agent.id },
          clientAuthConfig(agentSlug)
        );
      } catch (err) {
        console.warn('[PIPEDA] consent record failed:', err?.message || err);
      }
    }
    if (startSeller) {
      await beginChatRef.current?.({
        startIntent: 'seller_guided',
        mode: 'text',
        forceNew: true,
      });
    }
  };

  useEffect(() => {
    if (!agentSlug || !client || !convId) return;
    api.post(
      '/clients/link-conversation',
      { conversation_id: convId },
      clientAuthConfig(agentSlug)
    ).catch(() => {});
  }, [agentSlug, client, convId]);

  useEffect(() => {
    startedRef.current = started;
  }, [started]);

  const savedSearchKey = useMemo(
    () => formatBuyerCriteriaLine(client?.buyer_profile) || '',
    [client?.buyer_profile]
  );
  const hasSavedSearch = Boolean(client && savedSearchKey);
  const isSellerClient = client?.client_role === 'seller' || client?.client_role === 'both';
  const isReturningClient = Boolean(client && (hasSavedSearch || isSellerClient));
  const isBuyerClient = client?.client_role === 'buyer' || client?.client_role === 'both';

  // New-match count for the "Welcome back" screen — buyer side only, and only
  // worth a fetch once there's an actual saved search to have matched against.
  const [newMatchCount, setNewMatchCount] = useState(0);
  useEffect(() => {
    if (!isReturningClient || !isBuyerClient || !hasSavedSearch || !agentSlug) {
      setNewMatchCount(0);
      return undefined;
    }
    let cancelled = false;
    api.get('/clients/me/matches?unseen=true', clientAuthConfig(agentSlug))
      .then((res) => {
        if (!cancelled) setNewMatchCount(res.data?.unseen_count || 0);
      })
      .catch(() => {
        if (!cancelled) setNewMatchCount(0);
      });
    return () => { cancelled = true; };
  }, [isReturningClient, isBuyerClient, hasSavedSearch, agentSlug]);

  // Saved/passed listing states keyed by mls_number || property id.
  const [propertyStates, setPropertyStates] = useState({});
  // True only once the buyer saves a listing in THIS session. The save-search
  // card keys off this rather than propertyStates, because anonymous states
  // persist in localStorage across sessions — a save from last week must not
  // pop the card before Ava's first message in a fresh chat.
  const [sessionListingSave, setSessionListingSave] = useState(false);
  // Properties with a showing already requested this session (uppercase MLS
  // numbers). Their cards lock into a buttonless "showing requested" view.
  const [bookedKeys, setBookedKeys] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    if (!agentSlug) {
      setPropertyStates({});
      return undefined;
    }

    if (!client) {
      // Anonymous: reflect saves/passes held in the local buffer.
      const buffer = readSavedBuffer(agentSlug);
      const map = {};
      Object.entries(buffer).forEach(([key, val]) => {
        if (val?.state) map[key] = { state: val.state };
      });
      setPropertyStates(map);
      return undefined;
    }

    // Signed in: flush any buffered anonymous saves to the account, then load.
    (async () => {
      const buffer = readSavedBuffer(agentSlug);
      const keys = Object.keys(buffer);
      for (const key of keys) {
        const entry = buffer[key];
        if (entry?.state && entry?.property) {
          try {
            await api.post(
              '/clients/me/property-state',
              { state: entry.state, property: entry.property },
              clientAuthConfig(agentSlug)
            );
          } catch { /* ignore individual sync failures */ }
        }
      }
      if (keys.length) clearSavedBuffer(agentSlug);
      try {
        const res = await api.get('/clients/me/property-states', clientAuthConfig(agentSlug));
        if (cancelled) return;
        const map = {};
        (res.data?.states || []).forEach((row) => {
          const key = row.mls_number || row.property_id;
          if (key) map[key] = { state: row.state, property_id: row.property_id };
        });
        setPropertyStates(map);
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [agentSlug, client]);

  // "Not for me" answers Ava's question, not just the database — queue the
  // message and flush when she isn't mid-reply, so passing two cards quickly
  // doesn't silently drop the second reaction.
  const pendingCardMessagesRef = useRef([]);
  const [cardMsgTick, setCardMsgTick] = useState(0);
  // Speaks a fixed UI line (the heart confirmation) in voice sessions; set
  // once requestSpeechFallback is defined further down.
  const speakLineRef = useRef(null);

  const handlePropertyAction = useCallback(async (property, action, opts = {}) => {
    const key = property.mls_number || property.id;
    if (!key) return;
    if (action === 'passed' && !opts.silent) {
      const where = `${property.address}${property.city ? ` in ${property.city}` : ''}`;
      pendingCardMessagesRef.current.push(`The one on ${where} isn't for me.`);
      setCardMsgTick((t) => t + 1);
    }
    // A heart is not a chat turn: Ava confirms in one fixed line instead of
    // re-pitching the card, and the save-search card anchors under that line
    // instead of vanishing when a fresh reply arrives. Ava still sees the save
    // through the property-reaction record below.
    if (action === 'saved' && !opts.silent) {
      const line = `Saved ${property.address || 'that home'} to your list.`;
      setMessages((m) => {
        // Keep Ava's reply chips: they render from the last assistant message.
        const prevAva = [...m].reverse().find((x) => x.role === 'assistant');
        return [...m, {
          role: 'assistant', content: line, id: `heart-${Date.now()}`, quick_replies: prevAva?.quick_replies,
        }];
      });
      if (isConversationVoiceMode()) speakLineRef.current?.(line);
    }
    const prev = propertyStates;
    const existing = prev[key];
    const payload = {
      id: property.id,
      mls_number: property.mls_number,
      address: property.address,
      city: property.city,
      province: property.province,
      list_price: property.list_price,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      square_feet: property.square_feet,
      property_type: property.property_type,
      public_remarks: property.public_remarks,
    };

    if (action === 'clear') {
      setPropertyStates((s) => {
        const next = { ...s };
        delete next[key];
        return next;
      });
      if (!client) {
        removeSavedBufferEntry(agentSlug, key);
        if (convId && agent?.id) {
          api.post(`/conversations/public/${convId}/property-reaction`, {
            agent_id: agent.id,
            state: 'clear',
            property: payload,
          }).catch(() => {});
        }
        return;
      }
      try {
        if (existing?.property_id) {
          await api.delete(
            `/clients/me/property-state/${existing.property_id}`,
            clientAuthConfig(agentSlug)
          );
        }
      } catch {
        setPropertyStates(prev);
      }
      return;
    }

    setPropertyStates((s) => ({ ...s, [key]: { state: action, property_id: existing?.property_id } }));
    if (action === 'saved' && !opts.silent) {
      if (savePromptDismissed) setPostDismissSaveSignal(true);
      else setSessionListingSave(true);
    }

    if (!client) {
      // Buffer locally; flushed to the account on sign-in/registration.
      writeSavedBufferEntry(agentSlug, key, { state: action, property: payload });
      // Also persist on the conversation so Ava sees hearts/passes this session.
      if (convId && agent?.id) {
        api.post(`/conversations/public/${convId}/property-reaction`, {
          agent_id: agent.id,
          state: action,
          property: payload,
        }).catch(() => {});
      }
      return;
    }

    try {
      const res = await api.post(
        '/clients/me/property-state',
        { state: action, property: payload },
        clientAuthConfig(agentSlug)
      );
      setPropertyStates((s) => ({ ...s, [key]: { state: action, property_id: res.data?.property_id } }));
    } catch {
      setPropertyStates(prev);
    }
  }, [client, agentSlug, propertyStates, convId, agent, isConversationVoiceMode]);

  // Booking from a listing card: record interest, then ask Ava to book the
  // chosen (placeholder) time. The reply's booking_data drives the in-thread
  // confirmation + human-handoff note.
  const handlePropertyBook = useCallback((property) => {
    handlePropertyAction(property, 'saved', { silent: true });
    const where = `${property.address}${property.city ? `, ${property.city}` : ''}`;
    // Voice flag comes from conversation mode inside submitUserText — do not hardcode text.
    // Ava answers with the booking picker (open times + typed contact form).
    submitUserTextRef.current?.(`I'd like to book a showing for ${where}.`);
  }, [handlePropertyAction]);

  const handlePropertyTellMore = useCallback((property) => {
    const where = `${property.address}${property.city ? `, ${property.city}` : ''}`;
    submitUserTextRef.current?.(`Tell me more about ${where}.`, {
      mlsNumber: property.mls_number || null,
      propertyId: property.id || null,
    });
  }, []);

  // Flush queued card reactions ("not for me") into the conversation when Ava
  // isn't busy — after a short grace window, so someone passing on two cards
  // back-to-back gets ONE combined message instead of Ava replying to the
  // first pass before they reach the second card. Each new pass resets the
  // timer (cardMsgTick changes), extending the window.
  useEffect(() => {
    if (!pendingCardMessagesRef.current.length) return;
    if (sending || resumingChat || voiceStatus === 'transcribing') return;
    const timer = setTimeout(() => {
      if (!pendingCardMessagesRef.current.length) return;
      const queued = pendingCardMessagesRef.current.splice(0, pendingCardMessagesRef.current.length);
      // Inherit conversation voice mode (heart / pass must TTS in voice sessions).
      submitUserTextRef.current?.(queued.join(' '));
    }, 2000);
    return () => clearTimeout(timer);
  }, [cardMsgTick, sending, resumingChat, voiceStatus]);

  useEffect(() => {
    if (!client) {
      resumeDoneRef.current = false;
      resumeInFlightRef.current = false;
      userInitiatedNewChatRef.current = false;
      localChatActivityRef.current = false;
      autoFreshStartRef.current = false;
    }
  }, [client]);

  useEffect(() => {
    resumeDoneRef.current = false;
    resumeInFlightRef.current = false;
    userInitiatedNewChatRef.current = false;
    localChatActivityRef.current = false;
    autoFreshStartRef.current = false;
  }, [agentSlug]);

  const loadLinkedConversationMessages = useCallback(async () => {
    if (!agentSlug || !client) return [];
    let conversationId = getStoredConversationId(agentSlug, 'buyer')
      || getStoredConversationId(agentSlug, 'seller');
    if (!conversationId) {
      try {
        const latest = await api.get('/clients/me/conversations/latest', clientAuthConfig(agentSlug));
        conversationId = latest.data?.conversation?.conversation_id || null;
        if (conversationId) {
          setStoredConversationId(agentSlug, conversationId, 'buyer');
        }
      } catch {
        /* no linked chat */
      }
    }
    if (!conversationId) return [];

    const res = await api.get(
      `/clients/me/conversations/${conversationId}/messages`,
      clientAuthConfig(agentSlug)
    );
    return (res.data.messages || [])
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        featured_properties: m.featured_properties?.length ? m.featured_properties : undefined,
        area_links: m.area_links?.length ? m.area_links : undefined,
      }));
  }, [agentSlug, client]);

  const gatherRecapContext = useCallback(async () => {
    const authCfg = clientAuthConfig(agentSlug);
    let priorMessages = [];
    let buyerProfile = client?.buyer_profile;
    let showings = [];

    // Load prior messages only — do not sync-from-chat here (avoids re-applying
    // stale historical criteria on login/resume).
    try {
      priorMessages = await loadLinkedConversationMessages();
    } catch {
      /* optional */
    }

    try {
      const meRes = await api.get('/clients/me', authCfg);
      buyerProfile = meRes.data?.client?.buyer_profile || buyerProfile;
    } catch {
      /* ignore */
    }

    try {
      const showRes = await api.get('/clients/me/showings', authCfg);
      showings = showRes.data?.showings || [];
    } catch {
      /* ignore */
    }

    return { priorMessages, buyerProfile, showings };
  }, [agentSlug, client, loadLinkedConversationMessages]);

  const beginChatRef = useRef(null);
  const submitUserTextRef = useRef(null);

  const startFreshChatWithRecap = useCallback(async ({
    firstUserMessage = null,
    mlsNumber = null,
    propertyId = null,
    fromVoice = false,
    mode = null,
    startIntent = null,
  } = {}) => {
    if (!isReturningClient || !agent?.id || started || sending) return false;

    setResumingChat(true);
    try {
      const { priorMessages, buyerProfile, showings } = await gatherRecapContext();
      const recapContent = buildReturningClientRecap({
        clientRole: client.client_role || 'buyer',
        firstName: client.first_name,
        messages: priorMessages,
        buyerProfile,
        sellerProfile: client.seller_profile,
        showings,
        agentFirstName: agent.first_name || 'your agent',
      });
      const recapMessages = recapContent
        ? [{ id: 'return-recap', role: 'recap', content: recapContent }]
        : [];

      const resolvedMode = mode || chatMode || chatModeRef.current || (fromVoice ? 'voice' : 'text');
      const resolvedStartIntent = startIntent
        || resolveReturningClientStartIntent(client)
        || (resolvedMode === 'voice' ? 'voice_open' : 'text_open');

      if (!beginChatRef.current) return false;
      const pathMode = resolvedStartIntent === 'seller_guided' ? 'seller' : 'buyer';
      clearStoredConversationId(agentSlug, pathMode);
      return await beginChatRef.current({
        firstUserMessage,
        mlsNumber,
        propertyId,
        fromVoice,
        mode: resolvedMode,
        startIntent: resolvedStartIntent,
        recapMessages,
        forceNew: true,
        pathMode,
      });
    } finally {
      setResumingChat(false);
    }
  }, [
    agent?.first_name,
    agent?.id,
    agentSlug,
    client,
    gatherRecapContext,
    isReturningClient,
    sending,
    started,
    chatMode,
  ]);

  // Returning clients pick voice/text on the welcome screen — no auto-start from portal.

  const handleMessagesScroll = () => {
    if (!messagesContainerRef.current) return;
    // Ignore scrolls we initiate; any manual scroll suspends follow until the next turn.
    if (programmaticScrollRef.current) return;
    stickToBottomRef.current = false;
  };

  const scrollToLegal = () => {
    setShowConversationLegal(true);
    // Wait a tick so the block is actually in the DOM before scrolling to it.
    setTimeout(() => {
      legalCreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 0);
  };

  const scrollMessageTopIntoView = (msgId) => {
    const el = messagesContainerRef.current;
    if (!el || msgId == null) return;
    const target = el.querySelector(`[data-msg-id="${CSS.escape(String(msgId))}"]`);
    programmaticScrollRef.current = true;
    if (!target) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      const nextTop = el.scrollTop
        + (target.getBoundingClientRect().top - el.getBoundingClientRect().top);
      el.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
    }
    // Smooth scroll + card layout can keep firing onScroll after the first frame —
    // keep the programmatic guard long enough that those don't kill follow.
    window.setTimeout(() => { programmaticScrollRef.current = false; }, 700);
  };

  useEffect(() => {
    let focusMsg = null;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === 'user' || m.role === 'assistant') {
        focusMsg = m;
        break;
      }
    }

    // New user or assistant turn re-enables follow. Do not re-enable on `sending`
    // alone — that fights manual scroll while Ava is thinking. Assistant mount
    // must follow even on voice replies (TTS timing must not gate scroll).
    if (focusMsg?.role === 'user' || focusMsg?.role === 'assistant') {
      stickToBottomRef.current = true;
    }

    if (!stickToBottomRef.current || !focusMsg) return undefined;

    const cardCount = Array.isArray(focusMsg.featured_properties)
      ? focusMsg.featured_properties.length
      : 0;
    const scrollKey = `${focusMsg.id}:${cardCount}`;
    if (lastAutoScrollMsgIdRef.current === scrollKey) return undefined;
    lastAutoScrollMsgIdRef.current = scrollKey;

    // Scroll at render time (double-rAF), then again after cards typically mount.
    // Independent of audio playback — appendAssistantReply awaits TTS after setMessages.
    // "✓ Showing request sent" (and the save card) sit under the reply; show
    // them rather than the top of the reply.
    const endsWithSystemNote = messages[messages.length - 1]?.role === 'system';
    let cancelled = false;
    const run = () => {
      if (cancelled || !stickToBottomRef.current) return;
      if (endsWithSystemNote) {
        const el = messagesContainerRef.current;
        if (!el) return;
        programmaticScrollRef.current = true;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        window.setTimeout(() => { programmaticScrollRef.current = false; }, 700);
        return;
      }
      scrollMessageTopIntoView(focusMsg.id);
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    const t1 = window.setTimeout(run, 120);
    const t2 = window.setTimeout(run, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [messages, resumingChat]);

  useEffect(() => {
    return () => {
      if (playbackSourceRef.current) {
        try { playbackSourceRef.current.stop(); } catch (_) { /* already ended */ }
        playbackSourceRef.current = null;
      }
      if (playbackAudioRef.current) {
        playbackAudioRef.current.pause();
        playbackAudioRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      // Hands-free teardown on unmount.
      handsFreeRef.current = false;
      if (hfMonitorTimerRef.current) {
        clearInterval(hfMonitorTimerRef.current);
        hfMonitorTimerRef.current = null;
      }
      if (hfRecorderRef.current) {
        try { hfRecorderRef.current.stop(); } catch (_) { /* ignore */ }
        hfRecorderRef.current = null;
      }
      if (hfStreamRef.current) {
        hfStreamRef.current.getTracks().forEach(t => t.stop());
        hfStreamRef.current = null;
      }
      if (hfAudioCtxRef.current) {
        hfAudioCtxRef.current.close().catch(() => {});
        hfAudioCtxRef.current = null;
      }
      if (earconCtxRef.current) {
        earconCtxRef.current.close().catch(() => {});
        earconCtxRef.current = null;
      }
    };
  }, []);

  const agentName = agent ? `${agent.first_name} ${agent.last_name}` : '';
  const agentInitials = agent ? `${agent.first_name[0]}${agent.last_name[0]}` : '';
  const aiName = agent?.assistant_name || 'Ava';

  useEffect(() => {
    const desc = document.querySelector('meta[name="description"]');
    const previousTitle = document.title;
    const previousDesc = desc?.getAttribute('content') || '';
    if (agent?.first_name) {
      document.title = `${agentName}'s assistant · Ask ${aiName}`;
      desc?.setAttribute(
        'content',
        `Ask ${agent.first_name}'s assistant, ${aiName}, about homes. No account needed to start.`
      );
    }
    return () => {
      document.title = previousTitle;
      if (desc) desc.setAttribute('content', previousDesc);
    };
  }, [agent, agentName, aiName]);
  const aboutLinkLabel = String(agent?.about_link_label || '').trim() || `About ${agent?.first_name || 'the agent'}`;
  const brandPrimary = isHexColor(agent?.brand_primary_color) ? agent.brand_primary_color : '#C2410C';
  const brandSecondary = isHexColor(agent?.brand_secondary_color) ? agent.brand_secondary_color : '#9A3412';
  // Product chrome (indigo/amber) is fixed per the Ava widget spec; the agent's
  // brand colour is exposed only as --brand for Sarah's identity accents.
  const buyerThemeVars = {
    '--brand': brandPrimary,
    '--brand-2': brandSecondary,
    '--brand-soft': hexToRgba(brandPrimary, 0.12),
  };

  const stopPlayback = () => {
    const paused = bargeInPausedRef.current;
    bargeInPausedRef.current = null;
    if (paused?.ctx && paused.ctx.state === 'suspended') paused.ctx.resume().catch(() => {});
    const source = playbackSourceRef.current;
    if (source) {
      playbackSourceRef.current = null;
      try { source.stop(); } catch (_) { /* already ended */ }
    }
    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause();
      playbackAudioRef.current.currentTime = 0;
      playbackAudioRef.current = null;
    }
    if (playbackUrlRef.current) {
      URL.revokeObjectURL(playbackUrlRef.current);
      playbackUrlRef.current = null;
    }
    if (voiceStatus === 'playing') setVoiceStatus('idle');
  };

  /** Hold Ava's reply where it is; false when nothing was playing. */
  const pausePlaybackForBargeIn = () => {
    const ctx = playbackCtxRef.current;
    if (playbackSourceRef.current && ctx && ctx.state === 'running') {
      ctx.suspend().catch(() => {});
      bargeInPausedRef.current = { ctx };
      return true;
    }
    const audio = playbackAudioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      bargeInPausedRef.current = { audio };
      return true;
    }
    return false;
  };

  /** Carry on from where the barge-in paused her; false when there is nothing to resume. */
  const resumeBargeInPlayback = () => {
    const paused = bargeInPausedRef.current;
    bargeInPausedRef.current = null;
    if (paused?.ctx && playbackSourceRef.current) {
      paused.ctx.resume().catch(() => {});
      setVoiceStatus('playing');
      return true;
    }
    if (paused?.audio && playbackAudioRef.current === paused.audio) {
      paused.audio.play().catch(() => {});
      setVoiceStatus('playing');
      return true;
    }
    return false;
  };

  // Decode the whole clip before the first sample plays. Starting an MP3
  // while the browser is still reading the header makes the opening words
  // run ahead, then settle once the rate locks in.
  const playDecodedAudio = async (arrayBuffer, allowStart, volume = 1) => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx || !arrayBuffer?.byteLength) return false;
    if (!playbackCtxRef.current || playbackCtxRef.current.state === 'closed') {
      playbackCtxRef.current = new Ctx();
    }
    const ctx = playbackCtxRef.current;
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch (_) { /* play() fallback still available */ }
    }
    let decoded;
    try {
      decoded = await ctx.decodeAudioData(arrayBuffer.slice(0));
    } catch (err) {
      console.warn('[Voice] decodeAudioData failed:', err?.message || err);
      return false;
    }
    // Filler passes allowStart so a reply that landed during decode can
    // cancel before any sample plays — and before stopPlayback cuts it off.
    if (typeof allowStart === 'function' && !allowStart()) return false;
    if (ctx.state !== 'running') return false;
    stopPlayback();
    setVoiceStatus('playing');
    const source = ctx.createBufferSource();
    source.buffer = decoded;
    if (volume < 1) {
      const gain = ctx.createGain();
      gain.gain.value = volume;
      source.connect(gain);
      gain.connect(ctx.destination);
    } else {
      source.connect(ctx.destination);
    }
    playbackSourceRef.current = source;
    source.onended = () => {
      if (playbackSourceRef.current === source) {
        playbackSourceRef.current = null;
        setVoiceStatus('idle');
      }
    };
    source.start(0);
    return true;
  };

  const playAssistantAudio = async (audioBase64, mime = 'audio/mpeg') => {
    stopPlayback();
    setVoiceStatus('playing');
    try {
      const base64 = String(audioBase64 || '').replace(/\s+/g, '');
      console.log('[Voice] playAssistantAudio: start', {
        base64Chars: base64.length,
        mime: mime || null,
      });
      if (!base64) throw new Error('Empty audio payload');
      const safeMime = String(mime || 'audio/mpeg').split(';')[0].trim() || 'audio/mpeg';
      const mimeCandidates = safeMime === 'audio/mpeg'
        ? ['audio/mpeg', 'audio/mp3']
        : [safeMime];

      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      if (await playDecodedAudio(bytes.buffer)) return true;

      for (const candidateMime of mimeCandidates) {
        // Try Blob URL playback first (best browser compatibility).
        try {
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const blob = new Blob([bytes], { type: candidateMime });
          console.log('[Voice] playAssistantAudio: blob created', {
            mime: candidateMime,
            bytes: bytes.length,
            blobSize: blob.size,
          });
          const url = URL.createObjectURL(blob);
          playbackUrlRef.current = url;
          const audio = new Audio(url);
          playbackAudioRef.current = audio;
          const cleanup = () => {
            if (playbackUrlRef.current === url) {
              URL.revokeObjectURL(url);
              playbackUrlRef.current = null;
            }
            if (playbackAudioRef.current === audio) playbackAudioRef.current = null;
            setVoiceStatus('idle');
          };
          audio.onended = cleanup;
          audio.onerror = () => {
            console.warn('[Voice] playAssistantAudio: audio element error', {
              code: audio.error?.code,
              message: audio.error?.message,
            });
            cleanup();
          };
          await audio.play();
          console.log('[Voice] playAssistantAudio: play() resolved (blob)', { mime: candidateMime });
          return true;
        } catch (blobErr) {
          console.warn('[Voice] Blob playback failed, trying data URL:', {
            name: blobErr?.name,
            message: blobErr?.message || String(blobErr),
            notAllowed: blobErr?.name === 'NotAllowedError',
          });
        }

        // Fallback: data URL playback for strict media policy / MIME handling differences.
        try {
          const dataUrl = `data:${candidateMime};base64,${base64}`;
          const audio = new Audio(dataUrl);
          playbackAudioRef.current = audio;
          const cleanup = () => {
            if (playbackAudioRef.current === audio) playbackAudioRef.current = null;
            setVoiceStatus('idle');
          };
          audio.onended = cleanup;
          audio.onerror = () => {
            console.warn('[Voice] playAssistantAudio: data URL audio element error', {
              code: audio.error?.code,
              message: audio.error?.message,
            });
            cleanup();
          };
          await audio.play();
          console.log('[Voice] playAssistantAudio: play() resolved (data URL)', { mime: candidateMime });
          return true;
        } catch (dataErr) {
          console.warn('[Voice] Data URL playback failed:', {
            name: dataErr?.name,
            message: dataErr?.message || String(dataErr),
            notAllowed: dataErr?.name === 'NotAllowedError',
          });
        }
      }
      throw new Error('All playback strategies failed');
    } catch (e) {
      console.error('[Voice] Playback failed:', {
        name: e?.name,
        message: e?.message || String(e),
        notAllowed: e?.name === 'NotAllowedError',
      });
      stopPlayback();
      return false;
    }
  };

  const playAssistantAudioBuffer = async (bufferLike, mime = 'audio/mpeg', options = {}) => {
    const allowStart = options.allowStart;
    const volume = typeof options.volume === 'number' ? Math.min(1, Math.max(0, options.volume)) : 1;
    const gated = typeof allowStart === 'function';
    // Ungated playback (the real reply) replaces whatever is already playing.
    // Gated filler waits until it is actually allowed to start, so a fast
    // reply is not cut off by stopPlayback.
    if (!gated) {
      stopPlayback();
    }
    try {
      const safeMime = String(mime || 'audio/mpeg').split(';')[0].trim() || 'audio/mpeg';
      const data = bufferLike instanceof ArrayBuffer
        ? bufferLike
        : (ArrayBuffer.isView(bufferLike) ? bufferLike.buffer : null);
      console.log('[Voice] playAssistantAudioBuffer: start', {
        mime: safeMime,
        byteLength: data?.byteLength || 0,
      });
      if (!data || !data.byteLength) throw new Error('Empty audio buffer');
      if (await playDecodedAudio(data, gated ? allowStart : undefined, volume)) return true;
      if (gated && !allowStart()) return false;
      if (gated) {
        stopPlayback();
      }

      const blob = new Blob([data], { type: safeMime });
      console.log('[Voice] playAssistantAudioBuffer: blob created', {
        blobSize: blob.size,
        mime: safeMime,
      });
      const url = URL.createObjectURL(blob);
      playbackUrlRef.current = url;
      const audio = new Audio(url);
      audio.volume = volume;
      playbackAudioRef.current = audio;
      const cleanup = () => {
        if (playbackUrlRef.current === url) {
          URL.revokeObjectURL(url);
          playbackUrlRef.current = null;
        }
        if (playbackAudioRef.current === audio) playbackAudioRef.current = null;
        setVoiceStatus('idle');
      };
      audio.onended = cleanup;
      audio.onerror = () => {
        console.warn('[Voice] playAssistantAudioBuffer: audio element error', {
          code: audio.error?.code,
          message: audio.error?.message,
        });
        cleanup();
      };
      await audio.play();
      setVoiceStatus('playing');
      console.log('[Voice] playAssistantAudioBuffer: play() resolved');
      return true;
    } catch (e) {
      console.error('[Voice] Buffer playback failed:', {
        name: e?.name,
        message: e?.message || String(e),
        notAllowed: e?.name === 'NotAllowedError',
      });
      stopPlayback();
      return false;
    }
  };

  const requestSpeechFallback = async (text) => {
    const plain = String(text || '').trim();
    if (!plain) return false;
    try {
      console.log('[Voice] requestSpeechFallback: POST /voice/speak', {
        textChars: plain.length,
        agentId: Boolean(agent?.id),
      });
      const res = await api.post(
        '/voice/speak',
        { text: plain, agent_id: agent?.id },
        {
          responseType: 'arraybuffer',
          timeout: 20000,
          params: convIdRef.current ? { conversation_id: convIdRef.current } : undefined,
        }
      );
      console.log('[Voice] requestSpeechFallback: response', {
        status: res.status,
        contentType: res.headers?.['content-type'] || null,
        byteLength: res.data?.byteLength ?? res.data?.length ?? 0,
      });
      const played = await playAssistantAudioBuffer(
        res.data,
        res.headers?.['content-type'] || 'audio/mpeg'
      );
      return Boolean(played);
    } catch (e) {
      console.warn('[Voice] Fallback speak failed:', {
        status: e.response?.status,
        error: e.response?.data?.error || e.message,
        name: e?.name,
      });
      return false;
    }
  };

  speakLineRef.current = requestSpeechFallback;

  // Short spoken acknowledgements played on a voice turn while the real reply
  // is still thinking + synthesizing — closes the silent gap between "user
  // stops talking" and "Ava's answer starts playing". Deliberately vague —
  // never assume the user just asked a question or made a statement, since
  // we don't classify their message before picking one. (Rebuilt after demo
  // night's revert — the dropout that looked like it was caused by this
  // turned out to be unrelated; see [[Agents4Agents]] barge-in notes.)
  const FILLER_PHRASES = [
    'Okay, one moment.',
    'Give me a sec.',
    'Thanks, hang on one sec.',
    'One moment, please.',
    'Got it — just a sec.',
    'Okay, hang on.',
    'Just a moment.',
    'Alright, one sec.',
  ];

  const waitForCurrentPlaybackEnd = () => new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const source = playbackSourceRef.current;
    if (source) {
      const previous = source.onended;
      source.onended = (event) => {
        try { previous?.call(source, event); } catch (_) { /* ignore */ }
        finish();
      };
      const ms = Math.ceil((source.buffer?.duration || 2) * 1000) + 400;
      setTimeout(finish, ms);
      return;
    }
    const audio = playbackAudioRef.current;
    if (audio) {
      audio.addEventListener('ended', finish, { once: true });
      audio.addEventListener('error', finish, { once: true });
      const ms = Number.isFinite(audio.duration) && audio.duration > 0
        ? Math.ceil(audio.duration * 1000) + 400
        : 8000;
      setTimeout(finish, ms);
      return;
    }
    finish();
  });

  // maySpeak() is checked after TTS bytes are back, immediately before audio
  // starts. Returning false means the real reply won the race — skip the filler.
  const playFillerPhrase = (maySpeak) => {
    if (!agent?.id) return Promise.resolve();
    const choices = FILLER_PHRASES.length > 1
      ? FILLER_PHRASES.filter((p) => p !== lastFillerPhraseRef.current)
      : FILLER_PHRASES;
    const phrase = choices[Math.floor(Math.random() * choices.length)];
    lastFillerPhraseRef.current = phrase;
    return (async () => {
      try {
        const res = await api.post(
          '/voice/speak',
          { text: phrase, agent_id: agent.id },
          {
            responseType: 'arraybuffer',
            timeout: 8000,
            params: convIdRef.current ? { conversation_id: convIdRef.current } : undefined,
          }
        );
        const played = await playAssistantAudioBuffer(
          res.data,
          res.headers?.['content-type'] || 'audio/mpeg',
          { allowStart: maySpeak }
        );
        // audio.play() / source.start() resolve when sound starts, not when it
        // ends. Wait for the end so the real reply does not cut the filler off.
        if (played) await waitForCurrentPlaybackEnd();
      } catch (e) {
        console.warn('[Voice] Filler phrase failed (non-fatal):', e?.message || e);
      }
    })();
  };

  // Quiet looping texture that fills any remaining wait after the filler
  // phrase finishes speaking but the real reply still isn't back — same idea
  // as the thinking/typing ambience in Amy's own voice line. Deliberately a
  // separate audio element from playbackAudioRef: the real reply must be able
  // to cut this off instantly the moment it's ready, never wait for a loop
  // iteration to finish or fade out.
  const startThinkingAmbience = () => startSharedThinkingAmbience();
  const stopThinkingAmbience = () => stopSharedThinkingAmbience();

  // Waits FILLER_DELAY_MS before speaking a filler — an instant "one moment"
  // reads as robotic, and a reply that is already back needs no filler.
  // markReplied() runs the instant the real reply arrives.
  // - Not speaking yet (timer pending, or TTS still downloading): do not play
  //   the filler at all, and do not make the reply wait.
  // - Already speaking: let that clip finish so the reply does not cut it off.
  // Typing ambience keeps playing while she looks — filler speech does not
  // cut it off. markReplied always stops it so it cannot stay stuck on.
  const FILLER_DELAY_MS = 2000;

  const scheduleFillerPhrase = () => {
    if (!agent?.id) return { promise: Promise.resolve(), markReplied: () => {} };
    let startedSpeaking = false;
    let replied = false;
    let settled = false;
    let settle = () => {};
    const promise = new Promise((resolve) => { settle = resolve; });
    const finish = () => {
      if (settled) return;
      settled = true;
      settle();
    };
    const maySpeak = () => {
      if (replied) return false;
      startedSpeaking = true;
      return true;
    };
    const timer = setTimeout(() => {
      if (replied) { finish(); return; }
      playFillerPhrase(maySpeak).finally(() => {
        finish();
        if (!replied) startThinkingAmbience();
      });
    }, FILLER_DELAY_MS);
    return {
      promise,
      markReplied: () => {
        if (replied) return;
        replied = true;
        clearTimeout(timer);
        stopThinkingAmbience();
        // Reply won before any filler sample. Resolve now so the answer is
        // not held for a clip we are not going to play.
        if (!startedSpeaking) finish();
      },
    };
  };

  const persistDraftToConversation = async (conversationId, draft) => {
    if (!conversationId || !agent?.id || !draft || !Object.keys(draft).length) return;
    try {
      await api.patch(`/conversations/public/${conversationId}/draft-criteria`, {
        agent_id: agent.id,
        criteria: draft,
      });
    } catch (e) {
      console.warn('[BuyerChat] Draft criteria save:', e.response?.data?.error || e.message);
    }
  };

  const handleWizardComplete = (payload) => {
    try {
      localStorage.setItem(draftCriteriaStorageKey(agentSlug), JSON.stringify(payload));
    } catch { /* ignore */ }
    setDraftCriteria(payload);
    if (hasCoreSearchCriteria(payload)) setHasSearchCriteria(true);
    setShowCriteriaWizard(false);
    wizardStartMessageRef.current = summarizeWizardCriteriaForChat(payload);
  };

  const syncCriteriaFromChat = async (conversationId) => {
    if (!client || !conversationId) return false;
    try {
      const res = await api.post(
        '/clients/sync-from-chat',
        { conversation_id: conversationId },
        clientAuthConfig(agentSlug)
      );
      await reloadClient();
      if (res.data?.role_upgraded) {
        setMessages((m) => [
          ...m,
          {
            role: 'system',
            content: "You're buying and selling — your portal now shows both in one place. Open My portal anytime.",
            id: `role-upgrade-${Date.now()}`,
          },
        ]);
      }
      return Boolean(res.data?.role_upgraded);
    } catch (e) {
      console.warn('[Client] Criteria sync:', e.response?.data?.error || e.message);
      return false;
    }
  };

  const appendAssistantReply = async (replyData, conversationId, options = {}) => {
    const voiceTurn = resolveVoiceTurn(Boolean(options.fromVoice));
    const text = replyData?.message?.trim();
    if (!text) {
      console.warn('[BuyerChat] Empty assistant reply', replyData);
      stopThinkingAmbience();
      stopPlayback();
      setVoiceError(`${aiName} did not return a reply. Please try again.`);
      return;
    }
    const newMsgs = [{
      role: 'assistant',
      content: text,
      id: Date.now() + 1,
      offer_save_search: Boolean(replyData.offer_save_search),
      offer_save_consult: Boolean(replyData.offer_save_consult),
      quick_replies: replyData.quick_replies || undefined,
      area_prompt: replyData.quick_replies?.kind === 'area_regions',
      featured_properties: replyData.featured_properties?.length
        ? replyData.featured_properties
        : undefined,
      property_galleries: replyData.property_galleries?.length
        ? replyData.property_galleries
        : undefined,
      booking_picker: replyData.booking_picker || undefined,
      area_links: replyData.area_links?.length ? replyData.area_links : undefined,
    }];
    if (replyData.booking_data) {
      const bd = replyData.booking_data;
      const isShowing = Boolean(bd.mls_number || bd.property_address);
      const agentLabel = agent?.first_name || 'your agent';
      newMsgs.push({
        role: 'system',
        content: isShowing
          ? '✓ Showing request sent to your agent — they will follow up to confirm.'
          : `✓ Request sent — ${agentLabel} will follow up to confirm.`,
        id: Date.now() + 2,
      });
      // Lock this property's cards into the "showing requested" view.
      const { mls_number: bookedMls, property_address: bookedAddr } = bd;
      if (bookedMls || bookedAddr) {
        setBookedKeys((s) => {
          const next = new Set(s);
          if (bookedMls) next.add(String(bookedMls).toUpperCase());
          if (bookedAddr) next.add(String(bookedAddr).trim().toUpperCase());
          return next;
        });
      }
      if (savePromptDismissed) setPostDismissSaveSignal(true);
      else setSessionListingSave(true);
    }
    if (replyData.seller_consult_data) {
      newMsgs.push({
        role: 'home_profile',
        data: replyData.seller_consult_data,
        id: Date.now() + 3,
      });
      setSellerConsultComplete(true);
    }
    // Mount reply (+ cards) first so auto-scroll runs at render time — before TTS await.
    setMessages(m => [...m, ...newMsgs]);
    if (replyData.has_search_criteria) {
      setHasSearchCriteria(true);
    }
    if (replyData.criteria_snapshot && typeof replyData.criteria_snapshot === 'object') {
      setLiveCriteria((prev) => ({ ...prev, ...replyData.criteria_snapshot }));
      // Mirror into the same storage slot the wizard writes to, so criteria
      // captured through plain chat (not just the fast-path form) also rides
      // along as draft_criteria if this visitor registers/logs in later.
      if (agentSlug) {
        try {
          const key = draftCriteriaStorageKey(agentSlug);
          const existing = JSON.parse(localStorage.getItem(key) || '{}');
          localStorage.setItem(key, JSON.stringify({ ...existing, ...replyData.criteria_snapshot }));
        } catch { /* ignore */ }
      }
    }
    if (messageIsGoodbye(text)) {
      setChatVisitEnding(true);
      if (handsFreeRef.current) stopHandsFree({ silent: true });
      setChatMode('text');
      chatModeRef.current = 'text';
    }
    const shouldSpeakReply = voiceTurn;
    if (shouldSpeakReply) {
      // Let the filler phrase / ambience (if fired for this turn) finish
      // before the real reply's audio starts — this promise always resolves,
      // never rejects, so a failed filler never blocks the real reply.
      await fillerPlaybackPromiseRef.current;
      stopThinkingAmbience();
      console.log('[Voice] handleReplySpeech: decide', {
        fromVoice: Boolean(options.fromVoice),
        voiceTurn,
        chatMode: chatModeRef.current,
        handsFree: handsFreeRef.current,
        hasInlineAudio: Boolean(replyData.audio),
        audioChars: replyData.audio ? String(replyData.audio).length : 0,
        audioMime: replyData.audio_mime || null,
        ttsError: replyData.tts_error || null,
        ttsAvailable: replyData.tts_available,
      });
      // Provider missing: skip /speak, soft notice once, force text replies.
      if (replyData.tts_available === false) {
        if (!ttsUnavailableNotifiedRef.current) {
          ttsUnavailableNotifiedRef.current = true;
          setVoiceNotice("Voice replies aren't available right now — Ava will reply in text.");
          setVoiceError(null);
        }
        setChatMode('text');
        chatModeRef.current = 'text';
      } else if (replyData.tts_error && !replyData.audio) {
        setVoiceError(replyData.tts_error);
      } else if (replyData.audio) {
        const played = await playAssistantAudio(replyData.audio, replyData.audio_mime);
        if (!played) {
          // Genuine playback failure — do not round-trip /speak.
          setVoiceError(
            'Reply came through in chat, but audio could not play. You can keep chatting by text or try voice again.'
          );
        }
      } else {
        // Audio missing but TTS supposedly available → try /speak once.
        console.log('[Voice] handleReplySpeech: no inline audio → fallback /voice/speak');
        const played = await requestSpeechFallback(replyData.spoken_text || text);
        if (!played) {
          setVoiceError(
            replyData.tts_error
              || 'Reply came through in chat, but audio could not play. You can keep chatting by text or try voice again.'
          );
        }
      }
    } else {
      stopThinkingAmbience();
    }
    syncCriteriaFromChat(conversationId);
  };

  const chooseBranch = async (branch) => {
    if (!convId || !agent?.id || sending || !['buyer', 'seller'].includes(branch)) return false;
    setChatBranch(branch);
    pathModeRef.current = branch === 'seller' ? 'seller' : 'buyer';
    // Neutral "New conversation" threads are parked on the buyer key until the
    // user picks a path — move the pointer when they choose Selling.
    if (branch === 'seller') {
      const buyerId = getStoredConversationId(agentSlug, 'buyer');
      if (buyerId === convId) clearStoredConversationId(agentSlug, 'buyer');
      setStoredConversationId(agentSlug, convId, 'seller');
    } else {
      setStoredConversationId(agentSlug, convId, 'buyer');
    }
    setSending(true);
    setVoiceError(null);
    const authCfg = clientAuthConfig(agentSlug);
    try {
      const activeMode = chatModeRef.current || chatMode || 'text';
      const res = await api.post(`/conversations/public/${convId}/choose-branch`, {
        agent_id: agent.id,
        branch,
        mode: activeMode,
        handsFree: Boolean(handsFreeRef.current),
      }, authCfg);
      const userMsg = res.data.user_message || (branch === 'seller'
        ? "I'm thinking about selling a home"
        : "I'm looking to buy a home");
      const branchMsg = res.data.branch_message || '';
      setMessages((m) => [
        ...m,
        { role: 'user', content: userMsg, id: `branch-user-${Date.now()}` },
        { role: 'assistant', content: branchMsg, id: `branch-ai-${Date.now()}` },
      ]);
      if (res.data?.role_upgraded) {
        await reloadClient();
        setMessages((m) => [
          ...m,
          {
            role: 'system',
            content: "You're buying and selling — your portal now shows both in one place. Open My portal anytime.",
            id: `role-upgrade-${Date.now()}`,
          },
        ]);
      }
      if ((activeMode === 'voice' || handsFreeRef.current) && branchMsg.trim()) {
        const played = await requestSpeechFallback(branchMsg.trim());
        if (!played) {
          setVoiceError(`${aiName}'s reply is in the chat — audio could not play.`);
        }
      }
      return true;
    } catch (err) {
      console.error(err);
      // Roll back the optimistic branch so the header/UI doesn't show seller
      // or buyer chrome for a choice the server never recorded.
      setChatBranch(null);
      setVoiceError('Something went wrong. Please try again.');
      return false;
    } finally {
      setSending(false);
    }
  };

  const beginChat = async ({
    firstUserMessage = null,
    mlsNumber = null,
    propertyId = null,
    fromVoice = false,
    startIntent = null,
    mode = null,
    recapMessages = [],
    forceNew = false,
    carryDraft = false,
    /** When true, hand off to another path even if a conversation is already open (e.g. seller → buyer card tap). */
    allowWhenStarted = false,
    pathMode: pathModeArg = null,
  } = {}) => {
    if (!agent?.id || sending) return false;
    if (pipedaAckedRef.current !== true) return false;

    const resolvedMode = mode || chatMode || chatModeRef.current || (fromVoice ? 'voice' : 'text');
    const resolvedStartIntent = startIntent
      || (resolvedMode === 'voice' ? 'voice_open' : 'text_open');
    const pathMode = normalizeConvPathMode(
      pathModeArg
      || (resolvedStartIntent === 'seller_guided' ? 'seller' : 'buyer')
    );

    // Fresh conversations in text mode never inherit hands-free from a prior thread.
    if (forceNew && resolvedMode === 'text' && !fromVoice) {
      handsFreeRef.current = false;
      setHandsFree(false);
      clearLegacyHandsFreePreference(agentSlug);
      if (convIdRef.current) clearHandsFreePreference(agentSlug, convIdRef.current);
    }

    // A fresh landing thread does not inherit an unsaved search from the last visit.
    // The criteria wizard opts in with carryDraft.
    if (forceNew && !carryDraft && agentSlug) {
      try {
        localStorage.removeItem(draftCriteriaStorageKey(agentSlug));
      } catch { /* ignore */ }
      setDraftCriteria(null);
      setHasSearchCriteria(false);
    }

    // Homepage entry, or intentional path switch from an open conversation.
    const useHandoffNavigate = !isConversationView || allowWhenStarted;
    if (useHandoffNavigate) {
      if (started && !allowWhenStarted) return false;
      const openingText = Boolean(firstUserMessage?.trim())
        && resolvedMode !== 'voice'
        && !fromVoice;
      if (openingText && wantsSpokenFiller(firstUserMessage)) startThinkingAmbience();
      userInitiatedNewChatRef.current = true;
      resumeDoneRef.current = true;
      setResumingChat(false);
      setVoiceError(null);
      stopPlayback();
      setSending(true);
      setChatVisitEnding(false);
      setChatMode(resolvedMode);
      chatModeRef.current = resolvedMode;
      pathModeRef.current = pathMode;
      try {
        if (forceNew || resolvedStartIntent === 'seller_guided') {
          clearStoredConversationId(agentSlug, pathMode);
        }
        // forceNew: landing starter, card, typed opener, or hands-free.
        // A refresh of /conversation/:id still reloads that thread from the URL.
        let id = forceNew || resolvedStartIntent === 'seller_guided'
          ? null
          : getStoredConversationId(agentSlug, pathMode);
        if (id && agent?.id) {
          try {
            await fetchPublicConversation(id, agent.id, agentSlug, { attempts: 1 });
          } catch (err) {
            if (err?.response?.status === 404) {
              clearStoredConversationId(agentSlug, pathMode);
              id = null;
            }
          }
        }
        const guidedStart = resolvedStartIntent === 'buyer_guided' || resolvedStartIntent === 'seller_guided';
        const shouldSendFirstMessage = Boolean(firstUserMessage?.trim()) && (
          guidedStart ? !isOnlyBranchIntent(firstUserMessage) : true
        );

        if (!id) {
          const res = await api.post('/conversations/public', {
            agent_id: agent.id,
            channel: 'web_chat',
            start_intent: resolvedStartIntent,
            mode: resolvedMode,
            skip_greeting: shouldSendFirstMessage,
            consent_acknowledged: pipedaAckedRef.current === true,
            handsFree: Boolean(handsFreeRef.current),
            tester: testerMode === true,
            ...(client?.first_name ? { known_first_name: client.first_name } : {}),
          }, clientAuthConfig(agentSlug));
          id = res.data.conversation.id;
          setStoredConversationId(agentSlug, id, pathMode);
          if (
            pathMode !== 'seller'
            && carryDraft
            && draftCriteria
            && Object.keys(draftCriteria).length
          ) {
            await persistDraftToConversation(id, draftCriteria);
          }
        } else {
          setStoredConversationId(agentSlug, id, pathMode);
        }

        const handoff = {
          pendingMessage: shouldSendFirstMessage ? String(firstUserMessage).trim() : null,
          pendingMlsNumber: shouldSendFirstMessage && mlsNumber ? String(mlsNumber).trim() : null,
          pendingPropertyId: shouldSendFirstMessage && propertyId ? String(propertyId).trim() : null,
          fromVoice: Boolean(fromVoice || resolvedMode === 'voice'),
          mode: resolvedMode,
          handsFree: Boolean(handsFreeRef.current),
          // Carried across the same route remount as handsFree, below — without
          // this, the just-granted consent gate reappears on the conversation
          // route even though the mic keeps recording per the restored handsFree flag.
          pipedaAcked: pipedaAckedRef.current === true,
          startIntent: resolvedStartIntent,
          pathMode,
          recapMessages,
        };
        // Persist hands-free only for this conversation id (not agent-wide).
        setHandsFreePreference(agentSlug, handsFreeRef.current, id);
        stashHandoff(agentSlug, handoff);
        if (allowWhenStarted) {
          hydrateDoneRef.current = null;
        }
        navigate(`/chat/${agentSlug}/conversation/${id}`, {
          replace: Boolean(allowWhenStarted),
          state: handoff,
        });
        return true;
      } catch (err) {
        console.error(err);
        stopThinkingAmbience();
        setVoiceError('Something went wrong. Please try again.');
        return false;
      } finally {
        setSending(false);
      }
    }

    if (started) return false;
    userInitiatedNewChatRef.current = true;
    resumeDoneRef.current = true;
    setResumingChat(false);
    setVoiceError(null);
    stopPlayback();
    setStarted(true);
    setSending(true);
    setChatMode(resolvedMode);
    chatModeRef.current = resolvedMode;
    pathModeRef.current = pathMode;
    if (resolvedStartIntent === 'seller_guided') setChatBranch('seller');
    if (resolvedStartIntent === 'buyer_guided') setChatBranch('buyer');
    try {
      const authCfg = clientAuthConfig(agentSlug);
      const guidedStart = resolvedStartIntent === 'buyer_guided' || resolvedStartIntent === 'seller_guided';
      const shouldSendFirstMessage = Boolean(firstUserMessage?.trim()) && (
        guidedStart ? !isOnlyBranchIntent(firstUserMessage) : true
      );
      const res = await api.post('/conversations/public', {
        agent_id: agent.id,
        channel: 'web_chat',
        start_intent: resolvedStartIntent,
        mode: resolvedMode,
        skip_greeting: shouldSendFirstMessage,
        consent_acknowledged: pipedaAckedRef.current === true,
        handsFree: Boolean(handsFreeRef.current),
        tester: testerMode === true,
        ...(client?.first_name ? { known_first_name: client.first_name } : {}),
      }, authCfg);
      const id = res.data.conversation.id;
      setConvId(id);
      setStoredConversationId(agentSlug, id, pathMode);
      setHandsFreePreference(agentSlug, handsFreeRef.current, id);
      if (
        pathMode !== 'seller'
        && carryDraft
        && draftCriteria
        && Object.keys(draftCriteria).length
      ) {
        await persistDraftToConversation(id, draftCriteria);
        if (hasCoreSearchCriteria(draftCriteria)) setHasSearchCriteria(true);
      }
      const greetingText = res.data.greeting_message || '';
      setMessages([
        ...recapMessages,
        ...(greetingText.trim()
          ? [{ role: 'assistant', content: greetingText, id: 'greeting' }]
          : []),
      ]);
      // Never auto-play TTS on a conversation's opening turn — voice replies
      // still speak after the buyer talks (voice_turn / chatMode voice).
      if (shouldSendFirstMessage) {
        setMessages((m) => [...m, { role: 'user', content: firstUserMessage.trim(), id: Date.now() }]);
        const reply = await api.post(`/conversations/public/${id}/message`, {
          content: firstUserMessage.trim(),
          agent_id: agent.id,
          voice_turn: fromVoice || resolvedMode === 'voice',
          mode: resolvedMode,
          handsFree: Boolean(handsFreeRef.current),
          ...(mlsNumber ? { mls_hint: String(mlsNumber).trim() } : {}),
          ...(propertyId ? { property_id_hint: String(propertyId).trim() } : {}),
        }, authCfg);
        await appendAssistantReply(reply.data, id, {
          fromVoice: fromVoice || resolvedMode === 'voice',
        });
      }
      return true;
    } catch (err) {
      console.error(err);
      stopThinkingAmbience();
      setVoiceError('Something went wrong. Please try again.');
      setStarted(false);
      setMessages([]);
      setConvId(null);
      return false;
    } finally {
      setSending(false);
    }
  };

  beginChatRef.current = beginChat;

  const startNewConversation = async () => {
    if (!agent?.id || !agentSlug || sending || resumingChat) return;
    const agentLabel = agent.first_name || 'Sarah';
    const ok = window.confirm(
      `Start a new conversation? Your current chat will still be saved for ${agentLabel}.`
    );
    if (!ok) return;

    // Back to the same start as the homepage (Lillian, 2026-10-25): the
    // landing greeting, "Type a question, or tap Just talk", and the buyer's
    // first words open the thread. No buying/selling opener, and consent
    // already given this visit carries over.
    carryConsentRef.current = pipedaAckedRef.current === true;
    pathModeRef.current = 'buyer';
    hydrateDoneRef.current = null;
    lastAutoScrollMsgIdRef.current = null;
    setChatBranch(null);
    setHasSearchCriteria(false);
    setLiveCriteria(null);
    setPropertyStates({});
    setBookedKeys(new Set());
    setSessionListingSave(false);
    setPostDismissSaveSignal(false);
    setSavePromptDismissed(false);
    setSaveCardAutoShown(false);
    setSaveCardStrongReofferUsed(false);
    setSaveCardAnchorId(null);
    clearSaveCardSessionFlags(agentSlug);
    exitChatVisit();
  };

  const exitChatVisit = () => {
    stopPlayback();
    stopThinkingAmbience();
    if (handsFreeRef.current) stopHandsFree({ silent: true });
    handsFreeRef.current = false;
    setHandsFree(false);
    setChatMode('text');
    chatModeRef.current = 'text';
    setChatVisitEnding(false);
    if (agentSlug) {
      if (convId) clearHandsFreePreference(agentSlug, convId);
      clearLegacyHandsFreePreference(agentSlug);
      clearStoredConversationId(agentSlug);
    }
    setStarted(false);
    setMessages([]);
    setConvId(null);
    setVoiceError(null);
    navigate(`/chat/${agentSlug}`, { replace: true });
  };

  const resetTesterChat = async () => {
    if (!agentSlug) return;
    stopPlayback();
    stopThinkingAmbience();
    if (handsFreeRef.current) stopHandsFree({ silent: true });
    handsFreeRef.current = false;
    clearBuyerDemoSession(agentSlug);
    // A signed-in tester's hearts live on the account, so the browser clear
    // above leaves them lit in the next demo (Lillian, 2026-10-26).
    if (client) {
      try {
        const res = await api.get('/clients/me/property-states', clientAuthConfig(agentSlug));
        await Promise.all((res.data?.states || []).map((row) => api.delete(
          `/clients/me/property-state/${row.property_id}`,
          clientAuthConfig(agentSlug),
        ).catch(() => {})));
      } catch { /* the reset still goes ahead */ }
    }
    window.location.assign(testerLandingHref(agentSlug));
  };

  // Conversation page: rehydrate transcript + process pending handoff message.
  useEffect(() => {
    if (!isConversationView || !routeConversationId || !agent?.id || !agentSlug) return undefined;
    if (hydrateDoneRef.current === routeConversationId) return undefined;
    let cancelled = false;

    (async () => {
      hydrateDoneRef.current = routeConversationId;
      setStarted(true);
      setConvId(routeConversationId);
      setResumingChat(true);
      setVoiceError(null);

      const handoffFromState = location.state && typeof location.state === 'object'
        ? location.state
        : null;
      const handoff = handoffFromState || takeHandoff(agentSlug) || {};
      // Always burn sessionStorage once any handoff is consumed (state path used to skip takeHandoff).
      if (handoffFromState) {
        takeHandoff(agentSlug);
      }
      const isNeutralOpen = handoff.pathMode === 'neutral'
        || handoff.startIntent === 'text_open'
        || handoff.startIntent === 'voice_open';
      const initialPathMode = (handoff.startIntent === 'seller_guided' || handoff.pathMode === 'seller')
        ? 'seller'
        : 'buyer';
      pathModeRef.current = initialPathMode;
      setStoredConversationId(agentSlug, routeConversationId, initialPathMode);
      if (isNeutralOpen) {
        setChatBranch(null);
        pathModeRef.current = 'buyer';
      }
      const recapMessages = Array.isArray(handoff.recapMessages) ? handoff.recapMessages : [];
      const pending = handoff.pendingMessage?.trim() || null;
      const pendingMls = handoff.pendingMlsNumber?.trim() || null;
      const pendingPropertyId = handoff.pendingPropertyId?.trim() || null;
      const pendingId = pending ? `pending-${Date.now()}` : null;

      // Optimistic: show the buyer's first message while hydrate is in flight.
      if (pending || recapMessages.length) {
        setMessages([
          ...recapMessages,
          ...(pending ? [{ role: 'user', content: pending, id: pendingId }] : []),
        ]);
      }

      if (handoff.mode) {
        setChatMode(handoff.mode);
        chatModeRef.current = handoff.mode;
      }
      // This route remounts BuyerChat fresh, which would otherwise re-show the
      // PIPEDA gate a beat after the visitor already granted it on the landing
      // page — restore it from the same handoff package handsFree uses below.
      if (handoff.pipedaAcked) {
        grantPipeda(true);
      }
      // Restore hands-free only for THIS conversation:
      // - handoff.handsFree from an intentional HF start into this id, or
      // - per-conversation session flag (refresh / same-conversation handoff).
      // Never read the old agent-scoped a4a_handsfree_${slug} key.
      const restoreHandsFree = Boolean(
        handoff.handsFree || getHandsFreePreference(agentSlug, routeConversationId)
      );
      if (restoreHandsFree) {
        handsFreeRef.current = true;
        setHandsFree(true);
        setChatMode((m) => m || 'voice');
        chatModeRef.current = chatModeRef.current || 'voice';
        setHandsFreePreference(agentSlug, true, routeConversationId);
      } else {
        handsFreeRef.current = false;
        setHandsFree(false);
        clearHandsFreePreference(agentSlug, routeConversationId);
        if (!handoff.mode) {
          setChatMode('text');
          chatModeRef.current = 'text';
        }
      }
      if (isNeutralOpen) {
        setChatBranch(null);
        pathModeRef.current = 'buyer';
      } else if (handoff.startIntent === 'seller_guided' || handoff.pathMode === 'seller') {
        setChatBranch('seller');
        pathModeRef.current = 'seller';
      } else if (handoff.startIntent === 'buyer_guided' || handoff.pathMode === 'buyer') {
        setChatBranch('buyer');
        pathModeRef.current = 'buyer';
      }

      try {
        // Handoff window: 3 attempts over ~2s before treating as dead.
        const res = await fetchPublicConversation(
          routeConversationId,
          agent.id,
          agentSlug,
          { attempts: 3, delaysMs: [600, 900] }
        );
        if (cancelled) return;

        const kind = res.data?.conversation?.conversation_kind || '';
        grantPipeda(Boolean(res.data?.conversation?.consent_at));
        if (String(kind).includes('seller')) {
          setChatBranch('seller');
          pathModeRef.current = 'seller';
        } else if (handoff.startIntent !== 'seller_guided') {
          pathModeRef.current = 'buyer';
        }
        setStoredConversationId(agentSlug, routeConversationId, pathModeRef.current);

        const loaded = (res.data.messages || [])
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            featured_properties: m.featured_properties?.length ? m.featured_properties : undefined,
            property_galleries: m.property_galleries?.length ? m.property_galleries : undefined,
            area_links: m.area_links?.length ? m.area_links : undefined,
          }));

        const pendingAlreadyOnServer = Boolean(
          pending
          && loaded.some((m) => m.role === 'user' && String(m.content || '').trim() === pending)
        );
        setMessages([
          ...recapMessages,
          ...loaded,
          ...(!pendingAlreadyOnServer && pending
            ? [{ role: 'user', content: pending, id: pendingId }]
            : []),
        ]);
        if (threadWindingDownFromMessages(loaded) && !pending) {
          setChatVisitEnding(true);
          handsFreeRef.current = false;
          setHandsFree(false);
          clearHandsFreePreference(agentSlug, routeConversationId);
          setChatMode('text');
          chatModeRef.current = 'text';
        }

        if (pending && !pendingAlreadyOnServer && pipedaAckedRef.current) {
          const pendingVoice = resolveVoiceTurn(Boolean(handoff.fromVoice));
          const fillerHandle = (pendingVoice && wantsSpokenFiller(pending))
            ? scheduleFillerPhrase()
            : { promise: Promise.resolve(), markReplied: () => {} };
          if (wantsSpokenFiller(pending)) startThinkingAmbience();
          fillerPlaybackPromiseRef.current = fillerHandle.promise;
          setSending(true);
          try {
            const reply = await api.post(`/conversations/public/${routeConversationId}/message`, {
              content: pending,
              agent_id: agent.id,
              voice_turn: pendingVoice,
              mode: handoff.mode || chatModeRef.current || 'text',
              handsFree: Boolean(handsFreeRef.current),
              ...(pendingMls ? { mls_hint: pendingMls } : {}),
              ...(pendingPropertyId ? { property_id_hint: pendingPropertyId } : {}),
            }, clientAuthConfig(agentSlug));
            fillerHandle.markReplied();
            if (!cancelled) {
              await appendAssistantReply(reply.data, routeConversationId, {
                fromVoice: pendingVoice,
              });
            }
          } catch (err) {
            fillerHandle.markReplied();
            console.error(err);
            stopThinkingAmbience();
            if (!cancelled) setVoiceError('Something went wrong. Please try again.');
          } finally {
            if (!cancelled) setSending(false);
          }
        }

        // Clear navigation state so refresh doesn't re-send.
        if (handoffFromState) {
          navigate(location.pathname, { replace: true, state: null });
        }
      } catch (err) {
        console.error('[BuyerChat] hydrate failed:', err);
        hydrateDoneRef.current = null;
        clearStoredConversationId(agentSlug, pathModeRef.current);
        if (!cancelled) {
          setVoiceError("Something went wrong loading your chat — let's start over.");
          navigate(`/chat/${agentSlug}`, { replace: true });
        }
      } finally {
        if (!cancelled) setResumingChat(false);
      }
    })();

    return () => { cancelled = true; };
    // appendAssistantReply is stable enough via closure for this mount; avoid re-hydrate loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConversationView, routeConversationId, agent?.id, agentSlug]);

  const submitUserText = async (text, { fromVoice = false, skipSellerPathChoice = false, mlsNumber = null, propertyId = null, pathIntent = null, carryDraft = false, typedForm = false } = {}) => {
    if (!text.trim() || sending || !agent?.id) return;
    if (!pipedaAckedRef.current) {
      setVoiceError('Please confirm the privacy notice before sending.');
      return;
    }
    // Inherit conversation voice mode for UI actions; mic turns promote session to voice.
    const voiceTurn = resolveVoiceTurn(fromVoice);
    setResumingChat(false);
    setVoiceError(null);
    stopPlayback();
    stopThinkingAmbience();

    const wantsBuyerBrowse = pathIntent === 'buyer'
      || isBuyerBrowseIntent(text, { mlsNumber, propertyId, pathIntent });
    const onSellerPath = chatBranch === 'seller' || pathModeRef.current === 'seller';

    // Listing cards / browse chips must never continue a seller conversation.
    if (wantsBuyerBrowse && (onSellerPath || !started)) {
      if (started && onSellerPath) {
        setInput('');
        await beginChat({
          firstUserMessage: text,
          mlsNumber,
          propertyId,
          fromVoice: voiceTurn,
          startIntent: 'buyer_guided',
          mode: voiceTurn ? 'voice' : (chatModeRef.current || chatMode || 'text'),
          pathMode: 'buyer',
          allowWhenStarted: true,
        });
        return;
      }
      if (!started) {
        const resolvedMode = voiceTurn ? 'voice' : (chatMode || 'text');
        setInput('');
        if (isReturningClient) {
          await startFreshChatWithRecap({
            firstUserMessage: text.trim() || null,
            mlsNumber,
            propertyId,
            fromVoice: voiceTurn,
            mode: resolvedMode,
            startIntent: 'buyer_guided',
          });
          return;
        }
        await beginChat({
          firstUserMessage: text,
          mlsNumber,
          propertyId,
          fromVoice: voiceTurn,
          startIntent: 'buyer_guided',
          mode: resolvedMode,
          pathMode: 'buyer',
          forceNew: true,
          carryDraft,
        });
        return;
      }
    }

    // Choice gates: recognized answers route to the matching button action;
    // anything else falls through to Ava as a normal message — never hard-block
    // a reasonable typed or spoken answer ("just browsing", "not sure yet").
    if (started && conversationNeedsBranchChoice(messages)) {
      const branch = detectBranchFromText(text);
      if (branch) {
        setInput('');
        await chooseBranch(branch);
        return;
      }
    }

    if (!skipSellerPathChoice && started && conversationNeedsSellerPathChoice(messages)) {
      const path = detectSellerPathFromText(text);
      if (path) {
        setInput('');
        const pathText = sellerPathUserMessage(path, agent?.first_name);
        await submitUserText(pathText, { fromVoice: voiceTurn, skipSellerPathChoice: true });
        return;
      }
    }

    // Typed branch declarations ("I'm selling", "I want to buy") mid-chat:
    // update the UI classification so buyer-only elements (e.g. the save-search
    // card) don't appear on a seller path. The message itself still goes to Ava.
    if (started && !chatBranch && isOnlyBranchIntent(text)) {
      const declaredBranch = detectBranchFromText(text);
      if (declaredBranch) {
        setChatBranch(declaredBranch);
        pathModeRef.current = declaredBranch === 'seller' ? 'seller' : 'buyer';
      }
    }

    if (!started) {
      if (resumingChat) {
        setVoiceError('Still loading your conversation — one moment.');
        return;
      }
      if (isReturningClient) {
        const resolvedMode = voiceTurn ? 'voice' : (chatMode || 'text');
        setInput('');
        await startFreshChatWithRecap({
          firstUserMessage: text.trim() || null,
          mlsNumber,
          propertyId,
          fromVoice: voiceTurn,
          mode: resolvedMode,
        });
        return;
      }
      const branch = detectBranchFromText(text);
      // Mic / voice-mode turns keep spoken replies; pure text stays text until
      // the user enables Voice or hands-free.
      const resolvedMode = voiceTurn ? 'voice' : (chatMode || 'text');
      const startIntent = branch === 'seller'
        ? 'seller_guided'
        : branch === 'buyer'
          ? 'buyer_guided'
          : (resolvedMode === 'voice' ? 'voice_open' : 'text_open');
      await beginChat({
        firstUserMessage: text,
        mlsNumber,
        propertyId,
        fromVoice: voiceTurn,
        startIntent,
        mode: resolvedMode,
        pathMode: startIntent === 'seller_guided' ? 'seller' : 'buyer',
        forceNew: true,
        carryDraft,
      });
      return;
    }

    if (!convId) {
      setVoiceError('Chat is still starting. Please wait a moment and try again.');
      return;
    }

    localChatActivityRef.current = true;
    const userMsgId = Date.now();
    // Sending a message means they want to follow the conversation again.
    stickToBottomRef.current = true;
    setMessages(m => [...m, { role: 'user', content: text, id: userMsgId }]);
    setSending(true);
    setVoiceError(null);
    // Filler on longer / search-like voice turns. Short intake stays quiet.
    // Thinking ambience plays while she works — not only after a filler clip.
    const fillerHandle = (voiceTurn && wantsSpokenFiller(text))
      ? scheduleFillerPhrase()
      : { promise: Promise.resolve(), markReplied: () => {} };
    if (wantsSpokenFiller(text)) startThinkingAmbience();
    if (messageIsGoodbye(text)) {
      setChatVisitEnding(true);
      if (handsFreeRef.current) stopHandsFree({ silent: true });
      setChatMode('text');
      chatModeRef.current = 'text';
    }
    fillerPlaybackPromiseRef.current = fillerHandle.promise;
    try {
      const res = await api.post(
        `/conversations/public/${convId}/message`,
        {
          content: text,
          agent_id: agent.id,
          voice_turn: voiceTurn,
          mode: voiceTurn ? 'voice' : (chatModeRef.current || chatMode || undefined),
          handsFree: Boolean(handsFreeRef.current),
          ...(typedForm ? { typed_form: true } : {}),
          ...(mlsNumber ? { mls_hint: String(mlsNumber).trim() } : {}),
          ...(propertyId ? { property_id_hint: String(propertyId).trim() } : {}),
        },
        clientAuthConfig(agentSlug)
      );
      fillerHandle.markReplied();
      await appendAssistantReply(res.data, convId, {
        fromVoice: voiceTurn,
      });
    } catch (err) {
      fillerHandle.markReplied();
      stopThinkingAmbience();
      stopPlayback();
      console.error(err);
      const apiErr = err.response?.data?.error;
      // Remove the optimistic bubble and put their text back in the composer so
      // a failed send doesn't look ignored and retry doesn't duplicate it.
      setMessages((m) => m.filter((msg) => msg.id !== userMsgId));
      setInput(text);
      setVoiceError(apiErr || "That didn't send — please try again.");
    } finally {
      setSending(false);
    }
  };

  submitUserTextRef.current = submitUserText;

  // Portal match/saved "Ask Ava" → start a buyer thread about that listing
  // (same handoff featured listing cards use), instead of an empty welcome.
  useEffect(() => {
    if (askPropertyConsumedRef.current) return;
    if (isConversationView || started || sending || resumingChat) return;
    if (!agent?.id || clientAuthLoading) return;
    if (!pipedaAcked) return;

    const ask = parseAskPropertySearch(location.search);
    if (!ask) return;

    askPropertyConsumedRef.current = true;
    clearResumeRecap(agentSlug || slug);
    navigate(`/chat/${agentSlug || slug}`, { replace: true });

    const run = async () => {
      await submitUserTextRef.current?.(ask.prompt, {
        mlsNumber: ask.mlsNumber,
        propertyId: ask.propertyId,
        pathIntent: 'buyer',
      });
    };
    run().catch((err) => {
      console.error('[BuyerChat] Ask-property handoff failed:', err);
      askPropertyConsumedRef.current = false;
    });
  }, [
    agent?.id,
    agentSlug,
    slug,
    clientAuthLoading,
    isConversationView,
    location.search,
    navigate,
    resumingChat,
    sending,
    started,
    pipedaAcked,
  ]);

  // A typed reply means they want the thread quiet. The mic turns voice
  // back on the next time they hold it.
  const switchToTextReplies = () => {
    stopPlayback();
    if (handsFreeRef.current) stopHandsFree({ silent: true });
    chatModeRef.current = 'text';
    setChatMode('text');
  };

  const sendMessage = async () => {
    if (!input.trim() || sending || voiceStatus === 'transcribing') return;
    if (voiceStatus === 'recording') {
      // Hands-free listens most of the time — typed text wins over the open
      // mic: drop the current segment and send. Hold-to-talk still blocks.
      if (!handsFreeRef.current) return;
      cancelHandsFreeSegmentRef.current?.();
    }
    const text = input;
    setInput('');
    switchToTextReplies();
    await submitUserText(text, { fromVoice: false });
  };

  const getSpeechRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    return SR ? new SR() : null;
  };

  const stopSpeechRecognition = () => {
    const recognition = speechRecognitionRef.current;
    if (!recognition) return Promise.resolve();
    // Resolve once the recognizer fully ends so the FINAL transcript (not a
    // stale interim like "I'm" instead of "I'm open") is captured before submit.
    const ended = new Promise((resolve) => {
      recognitionEndResolveRef.current = resolve;
    });
    try {
      recognition.stop();
    } catch (_) { /* ignore */ }
    // Fallback timeout in case onend never fires.
    return Promise.race([ended, new Promise((r) => setTimeout(r, 900))]);
  };

  const startSpeechRecognition = ({ mirrorToInput = true } = {}) => {
    const recognition = getSpeechRecognition();
    if (!recognition) return false;

    liveTranscriptRef.current = '';
    recognition.continuous = true;
    recognition.interimResults = true;
    // Browser SpeechRecognition has no real auto-detect, unlike server-side
    // Whisper — was hardcoded to English, which mangled French speech
    // regardless of the buyer's actual language. Follow the browser's own
    // locale instead of forcing English, so a French-Canadian visitor's OS
    // language gets a fair shot.
    recognition.lang = navigator.language || 'en-CA';

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        const piece = event.results[i][0].transcript;
        // Android Chrome repeats the whole sentence so far in each result
        // ("My budget", "My budget is 300K"); replace instead of doubling it.
        // Compare without punctuation: "...downtown area." and "...downtown
        // area" are the same words (Lillian's hands-free test, 2026-10-25).
        const bare = (s) => s.toLowerCase().replace(/[^a-z0-9' ]+/g, ' ').replace(/\s+/g, ' ').trim();
        const sofar = bare(transcript);
        const now = bare(piece);
        if (!now) continue;
        if (sofar && now.startsWith(sofar)) transcript = piece;
        else if (sofar && sofar.endsWith(now)) continue;
        else transcript = transcript ? `${transcript.trim()} ${piece.trim()}` : piece;
      }
      liveTranscriptRef.current = transcript.trim();
      // Hold-to-talk mirrors captions into the composer (user can't type while
      // holding the mic). Hands-free shows them in the status bar instead so
      // anything the user typed is never overwritten.
      if (mirrorToInput) setInput(liveTranscriptRef.current);
      else setHfCaption(liveTranscriptRef.current);
    };

    recognition.onerror = (event) => {
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        console.warn('[Voice] Live caption:', event.error);
      }
    };

    recognition.onend = () => {
      if (speechRecognitionRef.current === recognition) {
        speechRecognitionRef.current = null;
      }
      if (recognitionEndResolveRef.current) {
        recognitionEndResolveRef.current();
        recognitionEndResolveRef.current = null;
      }
    };

    try {
      recognition.start();
      speechRecognitionRef.current = recognition;
      return true;
    } catch (err) {
      console.warn('[Voice] Could not start live captions:', err);
      return false;
    }
  };

  const getSupportedMimeType = () => {
    const candidates = [
      'audio/webm',
      'audio/webm;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    return candidates.find(t => MediaRecorder.isTypeSupported(t)) || '';
  };

  const ensureConversationForVoice = async () => {
    if (!pipedaAckedRef.current) {
      throw new Error('Please confirm the privacy notice before using voice.');
    }
    if (convIdRef.current) return convIdRef.current;
    if (!agent?.id) throw new Error('Voice is not ready yet.');
    const pathMode = pathModeRef.current || 'buyer';
    const res = await publicApi.post('/conversations/public', {
      agent_id: agent.id,
      channel: 'web_chat',
      start_intent: 'voice_open',
      mode: 'voice',
      skip_greeting: true,
      consent_acknowledged: true,
      ...(client?.first_name ? { known_first_name: client.first_name } : {}),
    }, clientAuthConfig(agentSlug));
    const id = res.data?.conversation?.id;
    if (!id) throw new Error('Could not start a voice session.');
    convIdRef.current = id;
    setConvId(id);
    setStoredConversationId(agentSlug, id, pathMode);
    return id;
  };

  const transcribeBlob = async (blob, uploadExt) => {
    const conversationId = await ensureConversationForVoice();
    const formData = new FormData();
    formData.append('audio', blob, `recording.${uploadExt}`);
    const base = process.env.REACT_APP_API_URL || '/api';
    // Rate-limit key + voice auth: query param, not body — multer hasn't parsed
    // the multipart body yet by the time those middleware run.
    const convIdQuery = `?conversation_id=${encodeURIComponent(conversationId)}`;
    const clientToken = agentSlug
      ? localStorage.getItem(clientTokenKey(agentSlug))
      : null;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    let res;
    try {
      res = await fetch(`${base}/voice/transcribe${convIdQuery}`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
        headers: clientToken ? { Authorization: `Bearer ${clientToken}` } : undefined,
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        throw new Error('Transcription timed out. Please try again.');
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.error || 'Could not transcribe audio');
      err.response = { data };
      throw err;
    }
    return data.text?.trim() || '';
  };

  const startRecording = async () => {
    if (!pipedaAckedRef.current) {
      setVoiceError('Please confirm the privacy notice before using voice.');
      return;
    }
    if (sending || voiceStatus === 'recording' || voiceStatus === 'transcribing' || !agent?.id) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceError('Voice is not supported in this browser.');
      return;
    }
    setVoiceError(null);
    stopPlayback();
    cancelRecordingRef.current = false;
    liveTranscriptRef.current = '';
    setInput('');
    startSpeechRecognition();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      if (cancelRecordingRef.current || !pendingStopRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      streamRef.current = stream;
      audioChunksRef.current = [];

      const mimeType = getSupportedMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      const recordMime = recorder.mimeType || mimeType || 'audio/webm';
      recordMimeRef.current = recordMime.split(';')[0] || 'audio/webm';
      uploadExtRef.current = recordMime.includes('mp4') ? 'mp4' : recordMime.includes('ogg') ? 'ogg' : 'webm';

      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        mediaRecorderRef.current = null;
        pendingStopRef.current = false;

        // Wait for the live recognizer to finish so we read the COMPLETE
        // transcript, not a mid-utterance interim result.
        await stopSpeechRecognition();

        if (cancelRecordingRef.current) {
          setInput('');
          setVoiceStatus('idle');
          return;
        }

        const blob = new Blob(audioChunksRef.current, { type: recordMimeRef.current });
        audioChunksRef.current = [];

        let transcript = liveTranscriptRef.current.trim();

        if (blob.size < 500 && !transcript) {
          setInput('');
          setVoiceStatus('idle');
          setVoiceError('Hold the mic a little longer while you speak.');
          return;
        }

        try {
          if (!transcript) {
            setVoiceStatus('transcribing');
            transcript = await transcribeBlob(blob, uploadExtRef.current);
          }
          if (!transcript) {
            setInput('');
            setVoiceError("Didn't catch that — try again.");
            setVoiceStatus('idle');
            return;
          }
          setInput('');
          setVoiceStatus('idle');
          await submitUserText(transcript, { fromVoice: true });
        } catch (err) {
          console.error('[Voice] Transcribe failed:', err);
          setInput('');
          // Only surface a backend-provided message — a raw fetch-level failure
          // (no err.response) would otherwise leak browser text like "Failed to fetch".
          setVoiceError(err.response?.data?.error || 'Could not transcribe audio. Please try again.');
          setVoiceStatus('idle');
        }
      };

      recorder.start(250);
      setVoiceStatus('recording');
    } catch (err) {
      console.error('[Voice] Mic access failed:', err);
      stopSpeechRecognition();
      setInput('');
      setVoiceError(describeMicError(err));
      setVoiceStatus('idle');
      pendingStopRef.current = false;
    }
  };

  const stopRecording = () => {
    stopSpeechRecognition();
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === 'recording') {
      try {
        recorder.requestData();
      } catch (_) { /* ignore */ }
      recorder.stop();
    }
  };

  const handleMicPointerDown = (e) => {
    e.preventDefault();
    if (micDisabled) return;
    pendingStopRef.current = true;
    cancelRecordingRef.current = false;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) { /* ignore */ }
    startRecording();
  };

  const handleMicPointerUp = (e) => {
    e.preventDefault();
    pendingStopRef.current = false;
    if (voiceStatus === 'recording' || mediaRecorderRef.current?.state === 'recording') {
      stopRecording();
    } else {
      cancelRecordingRef.current = true;
      stopSpeechRecognition();
      setInput('');
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (voiceStatus === 'recording') setVoiceStatus('idle');
    }
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) { /* ignore */ }
  };

  // ---------- Hands-free conversation mode ----------
  // Open mic, silence detection ends each turn, Ava replies aloud, the mic
  // reopens when she finishes — until the user taps stop. Built for eyes-free
  // use: audible earcons signal mic state, status text is aria-live.

  // Short two-tone beep so a blind user always knows when the mic opens/closes.
  const playEarcon = (kind) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!earconCtxRef.current || earconCtxRef.current.state === 'closed') {
        earconCtxRef.current = new Ctx();
      }
      const ctx = earconCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const tones = kind === 'off' ? [[660, 0], [440, 0.12]] : [[440, 0], [660, 0.12]];
      tones.forEach(([freq, at]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + at);
        gain.gain.exponentialRampToValueAtTime(0.07, now + at + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.12);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + at);
        osc.stop(now + at + 0.14);
      });
    } catch (_) { /* earcons are best-effort */ }
  };

  const teardownHandsFreeSegment = () => {
    if (hfMonitorTimerRef.current) {
      clearInterval(hfMonitorTimerRef.current);
      hfMonitorTimerRef.current = null;
    }
    if (hfAudioCtxRef.current) {
      hfAudioCtxRef.current.close().catch(() => {});
      hfAudioCtxRef.current = null;
    }
    const rec = hfRecorderRef.current;
    hfRecorderRef.current = null;
    if (rec) {
      try { if (rec.state !== 'inactive') rec.stop(); } catch (_) { /* ignore */ }
    }
    if (hfStreamRef.current) {
      hfStreamRef.current.getTracks().forEach(t => t.stop());
      hfStreamRef.current = null;
    }
    hfChunksRef.current = [];
    hfSegmentActiveRef.current = false;
  };

  const stopHandsFree = ({ silent = false, message = null } = {}) => {
    const wasOn = handsFreeRef.current;
    handsFreeRef.current = false;
    setHandsFree(false);
    if (agentSlug) {
      clearHandsFreePreference(agentSlug, convIdRef.current || convId);
      clearLegacyHandsFreePreference(agentSlug);
    }
    setHfMissNotice(false);
    setHfCaption('');
    hfFinishRequestedRef.current = false;
    stopSpeechRecognition();
    teardownHandsFreeSegment();
    stopThinkingAmbience();
    setVoiceStatus((s) => (s === 'recording' ? 'idle' : s));
    if (message) setVoiceError(message);
    if (wasOn && !silent) playEarcon('off');
  };

  // opts lets a confirmed barge-in hand off an already-running recorder that's
  // been capturing since before the interruption was even confirmed, instead
  // of starting fresh and losing whatever was said during the detection
  // window. Every normal call site (the reopen loop, etc.) passes nothing, so
  // this behaves exactly as before for them.
  const startHandsFreeSegment = async (opts = {}) => {
    const { existingStream, existingRecorder, existingChunks, existingMimeType, existingUploadExt } = opts;
    const fromBargeIn = Boolean(existingStream);
    let endedAsNoise = false;
    if (existingStream) {
      console.log('[Voice] startHandsFreeSegment: barge-in handoff call', {
        handsFree: handsFreeRef.current,
        hfSegmentActive: hfSegmentActiveRef.current,
      });
    }
    if (!handsFreeRef.current || hfSegmentActiveRef.current) {
      if (existingStream) {
        console.warn('[Voice] Barge-in handoff REJECTED at entry guard — stream/recorder now orphaned', {
          handsFree: handsFreeRef.current,
          hfSegmentActive: hfSegmentActiveRef.current,
        });
      }
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || !(window.AudioContext || window.webkitAudioContext)) {
      stopHandsFree({ silent: true, message: 'Just talk is not supported in this browser.' });
      return;
    }
    hfSegmentActiveRef.current = true;
    hfFinishRequestedRef.current = false;
    setVoiceError(null);
    liveTranscriptRef.current = '';
    setHfCaption('');

    let stream;
    if (existingStream) {
      stream = existingStream;
    } else {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
      } catch (err) {
        console.error('[Voice] Hands-free mic access failed:', err);
        hfSegmentActiveRef.current = false;
        stopHandsFree({ message: describeMicError(err, 'to talk') });
        return;
      }
    }
    if (!handsFreeRef.current) {
      stream.getTracks().forEach(t => t.stop());
      hfSegmentActiveRef.current = false;
      return;
    }
    hfStreamRef.current = stream;
    hfChunksRef.current = existingChunks ? existingChunks.slice() : [];
    startSpeechRecognition({ mirrorToInput: false });

    let recorder;
    let recordMime;
    let uploadExt;
    if (existingRecorder) {
      recorder = existingRecorder;
      recordMime = existingMimeType;
      uploadExt = existingUploadExt;
    } else {
      const mimeType = getSupportedMimeType();
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recordMime = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0] || 'audio/webm';
      uploadExt = recordMime.includes('mp4') ? 'mp4' : recordMime.includes('ogg') ? 'ogg' : 'webm';
    }
    hfRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) hfChunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      const finish = hfFinishRequestedRef.current;
      hfFinishRequestedRef.current = false;
      const chunks = hfChunksRef.current;
      hfChunksRef.current = [];
      if (hfRecorderRef.current === recorder) hfRecorderRef.current = null;
      if (hfStreamRef.current === stream) {
        stream.getTracks().forEach(t => t.stop());
        hfStreamRef.current = null;
      }
      hfSegmentActiveRef.current = false;
      if (!finish || !handsFreeRef.current) {
        if (bargeInPausedRef.current) stopPlayback();
        return;
      }

      await stopSpeechRecognition();
      let transcript = liveTranscriptRef.current.trim();
      liveTranscriptRef.current = '';
      let transcribeFailed = false;
      try {
        if (!transcript && !endedAsNoise) {
          const blob = new Blob(chunks, { type: recordMime });
          if (blob.size >= 500) {
            setVoiceStatus('transcribing');
            transcript = await transcribeBlob(blob, uploadExt);
          }
        }
      } catch (err) {
        console.error('[Voice] Hands-free transcribe failed:', err);
        transcribeFailed = true;
      }
      setHfCaption('');
      // "[clears throat]" or "(coughs)" is a noise, not words.
      if (fromBargeIn && !transcript.replace(/\[[^\]]*\]|\([^)]*\)/g, '').replace(/[^\p{L}\p{N}]/gu, '')) {
        transcript = '';
        if (!transcribeFailed && resumeBargeInPlayback()) return;
      }
      if (bargeInPausedRef.current) stopPlayback();
      setVoiceStatus('idle');
      if (!transcript) {
        if (transcribeFailed) {
          // A real STT/network failure must not look like silence — the buyer
          // would otherwise just keep repeating themselves against an outage.
          setHfMissNotice(false);
          setVoiceError('Had trouble hearing that — check your connection and try again.');
        } else {
          // Nothing intelligible — flag it and go straight back to listening
          // (the loop effect restarts a segment) instead of hanging silently.
          setHfMissNotice(true);
        }
        return;
      }
      setHfMissNotice(false);
      await submitUserText(transcript, { fromVoice: true });
    };

    // End the turn once we've heard speech followed by ~1.8s of quiet.
    const finishSegment = () => {
      if (hfFinishRequestedRef.current) return;
      hfFinishRequestedRef.current = true;
      if (hfMonitorTimerRef.current) {
        clearInterval(hfMonitorTimerRef.current);
        hfMonitorTimerRef.current = null;
      }
      if (hfAudioCtxRef.current) {
        hfAudioCtxRef.current.close().catch(() => {});
        hfAudioCtxRef.current = null;
      }
      try {
        if (recorder.state === 'recording') {
          recorder.requestData();
          recorder.stop();
        }
      } catch (_) { /* ignore */ }
    };

    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new Ctx();
      hfAudioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const segmentStartedAt = Date.now();
      let speechDetected = false;
      let speechStartedAt = 0;
      let lastVoiceAt = segmentStartedAt;
      // Adaptive noise floor: tracks the quietest level heard (drifting up very
      // slowly) so steady background noise — fan, HVAC, traffic — doesn't read
      // as continuous speech and leave the turn hanging open forever.
      let noiseFloor = 0.006;
      let lastTranscript = '';
      let transcriptStableAt = segmentStartedAt;

      hfMonitorTimerRef.current = setInterval(() => {
        if (!handsFreeRef.current || hfRecorderRef.current !== recorder) return;
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        const nowMs = Date.now();

        noiseFloor = rms < noiseFloor ? rms : noiseFloor * 0.995 + rms * 0.005;
        // Floors low enough for a laptop mic with noise suppression; Brave has no
        // live captions to fall back on (Lillian's desktop test, 2026-10-25).
        const startTh = Math.min(0.10, Math.max(0.02, noiseFloor * 3));
        const sustainTh = Math.min(0.05, Math.max(0.012, noiseFloor * 1.8));

        // Hysteresis: louder threshold to start "speech", quieter to sustain it,
        // so trailing off at the end of a sentence doesn't cut the turn early.
        if (rms > (speechDetected ? sustainTh : startTh)) {
          if (!speechDetected) speechStartedAt = nowMs;
          speechDetected = true;
          lastVoiceAt = nowMs;
        }

        // Second end-of-turn signal: when the live captions have words and they
        // stop changing, the person has stopped talking — even if room noise
        // keeps the mic level above the silence threshold.
        const currentTranscript = liveTranscriptRef.current;
        if (currentTranscript !== lastTranscript) {
          lastTranscript = currentTranscript;
          transcriptStableAt = nowMs;
          // Words in the captions are speech even when the level meter reads
          // quiet: on some phones the caption engine takes the mic and the
          // recorder hears near-silence (Lillian's Just talk test, 2026-10-25).
          if (currentTranscript) {
            if (!speechDetected) speechStartedAt = nowMs;
            speechDetected = true;
            lastVoiceAt = nowMs;
          }
        }

        const quietLongEnough = speechDetected && nowMs - lastVoiceAt > 1800;
        const captionsSettled = speechDetected && lastTranscript && nowMs - transcriptStableAt > 2200;
        // Hard cap: a single turn can never listen forever, whatever the room
        // sounds like — send what we have and let the user continue.
        const turnTooLong = speechDetected && nowMs - speechStartedAt > 45000;
        // A barge-in with no speech after it was a noise: end fast so Ava resumes.
        const bargeInWasNoise = fromBargeIn && !speechDetected && nowMs - segmentStartedAt > 2500;
        if (bargeInWasNoise) endedAsNoise = true;
        if (quietLongEnough || captionsSettled || turnTooLong || bargeInWasNoise) {
          finishSegment();
        } else if (!speechDetected && nowMs - segmentStartedAt > 40000) {
          stopHandsFree({ message: "I didn't hear anything, so I stopped listening. Tap Just talk when you're ready." });
        }
      }, 100);
    } catch (err) {
      console.error('[Voice] Hands-free level monitor failed:', err);
      stopHandsFree({ silent: true, message: 'Just talk is not supported in this browser.' });
      return;
    }

    if (!existingRecorder) {
      recorder.start(250);
    }
    setVoiceStatus('recording');
    // Skip the "listening" earcon on a barge-in handoff — the interruption
    // itself is already the audible cue; an extra beep right after cutting
    // Ava off mid-sentence would just be noise on top of noise.
    if (!existingRecorder) {
      playEarcon('listen');
    }
  };

  startHandsFreeSegmentRef.current = startHandsFreeSegment;

  // Abandon the current listening segment without sending anything — used when
  // the user types and sends while the hands-free mic is open. Hands-free stays
  // on; the loop reopens the mic after the reply.
  const cancelHandsFreeSegment = () => {
    hfFinishRequestedRef.current = false;
    stopSpeechRecognition();
    teardownHandsFreeSegment();
    setHfCaption('');
    setVoiceStatus((s) => (s === 'recording' ? 'idle' : s));
  };

  cancelHandsFreeSegmentRef.current = cancelHandsFreeSegment;

  const startHandsFree = async () => {
    if (!pipedaAckedRef.current) {
      setVoiceError('Please confirm the privacy notice before using voice.');
      return;
    }
    if (handsFreeRef.current) return;
    handsFreeRef.current = true;
    setHandsFree(true);
    if (agentSlug) setHandsFreePreference(agentSlug, true, convIdRef.current || convId);
    setVoiceError(null);
    setChatMode('voice');
    chatModeRef.current = 'voice';
    // Create/resume the earcon context on this tap — user gestures unlock audio.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx && !earconCtxRef.current) earconCtxRef.current = new Ctx();
      earconCtxRef.current?.resume?.().catch(() => {});
    } catch (_) { /* ignore */ }
    // A new buyer speaks first, as with typing or Hold: the mic opens on the
    // landing and their first words start the chat, so there is no chat (and
    // no buying-or-selling opener) until Ava has heard something.
    if (!isConversationView && !started && !sending && !resumingChat && isReturningClient) {
      await startFreshChatWithRecap({ mode: 'voice' });
    }
    // Persist against the conversation id once beginChat assigns it.
    if (agentSlug && (convIdRef.current || convId)) {
      setHandsFreePreference(agentSlug, true, convIdRef.current || convId);
    }
    // The loop effect below opens the mic once the greeting finishes playing.
  };

  const toggleHandsFree = () => {
    if (handsFree) stopHandsFree();
    else startHandsFree();
  };

  const chatBusy = sending || resumingChat;
  // "Not for me" takes a home off the row; the buyer set it aside.
  const homesInChat = useMemo(
    () => homesShownInChat(messages).filter((p) => propertyStates[p.mls_number || p.id]?.state !== 'passed'),
    [messages, propertyStates],
  );
  const repeatCardIds = useMemo(() => repeatCardMessageIds(messages), [messages]);
  // Landing is known immediately. A conversation URL stays blocked until hydrate
  // reports consent_at, so a direct link cannot send during the load gap.
  const pipedaBlocked = !pipedaAcked && (pipedaKnown || isConversationView);
  const micDisabled = chatBusy || voiceStatus === 'transcribing' || !agent?.id || handsFree || pipedaBlocked;
  // The main mic button's own enabled/disabled state, distinct from
  // micDisabled above: while hands-free is on, the button is deliberately
  // kept ENABLED so a single tap can exit hands-free (see the button's
  // onClick) instead of just sitting there inert — the exact confusion that
  // made it look broken. handleMicPointerDown/Up still separately guard on
  // micDisabled, so no press-and-hold recording can start during this state.
  const micButtonDisabled = handsFree ? false : micDisabled;
  const isSpeaking = voiceStatus === 'playing';

  // Keyboard push-to-talk: hold Home to record, matching the same key
  // already used for Amy's own voice line (backtalk's push-to-talk key) —
  // one consistent "hold this to talk to an AI" habit instead of a new key
  // to learn. Home was picked specifically because it's essentially never
  // held during normal typing (unlike Shift or Control, which real typing
  // and OS shortcuts use constantly) — safe to catch document-wide. Skipped
  // entirely while there's pending typed text, so an accidental Home press
  // while composing a message can't wipe it out and start recording instead
  // (sendEnabled isn't computed yet at this point in the component, so its
  // condition is inlined here rather than moving that declaration around).
  // Reuses startRecording/stopRecording directly — no pointer capture needed
  // for a keyboard hold, unlike the on-screen button.
  // NOTE: this hook must stay above the agentLoading/agentError early
  // returns further down — a hook placed after a conditional return changes
  // hook count between renders and breaks React outright.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Home' || e.repeat || homeKeyDownRef.current) return;
      const hasPendingText = Boolean(input.trim())
        && !chatBusy && voiceStatus !== 'transcribing'
        && (voiceStatus !== 'recording' || handsFree);
      if (micDisabled || hasPendingText) return;
      homeKeyDownRef.current = true;
      e.preventDefault();
      pendingStopRef.current = true;
      cancelRecordingRef.current = false;
      startRecording();
    };
    const onKeyUp = (e) => {
      if (e.key !== 'Home' || !homeKeyDownRef.current) return;
      homeKeyDownRef.current = false;
      e.preventDefault();
      pendingStopRef.current = false;
      if (voiceStatus === 'recording' || mediaRecorderRef.current?.state === 'recording') {
        stopRecording();
      } else {
        cancelRecordingRef.current = true;
        stopSpeechRecognition();
        setInput('');
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        if (voiceStatus === 'recording') setVoiceStatus('idle');
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [micDisabled, input, chatBusy, voiceStatus, handsFree]);

  // Hands-free loop driver: whenever the widget goes quiet (no recording, no
  // reply in flight, no TTS playing), reopen the mic after a short settle so
  // Ava's audio tail isn't captured. Cleared automatically if state changes.
  useEffect(() => {
    if (!handsFree || chatVisitEnding) return undefined;
    if (voiceStatus !== 'idle' || sending || resumingChat) return undefined;
    if (hfSegmentActiveRef.current) return undefined;
    const t = setTimeout(() => {
      if (handsFreeRef.current && !hfSegmentActiveRef.current) {
        startHandsFreeSegmentRef.current?.();
      }
    }, 400);
    return () => clearTimeout(t);
  }, [handsFree, voiceStatus, sending, resumingChat, chatVisitEnding]);

  // Barge-in: while hands-free is on and Ava is speaking, record continuously
  // from the moment she starts (not just listen) — so if the user talks over
  // her, nothing they said during the interruption-detection window is lost.
  // echoCancellation strips her own voice back out of the mic feed, so the
  // level check is meant to catch real speech, not her audio leaking back in
  // through speakers; a sustained threshold (not one blip) is a second layer
  // of protection against a false trigger from noise or imperfect echo
  // cancellation. On a real trigger: stop her audio and hand the already-
  // running recorder + everything captured so far to startHandsFreeSegment,
  // which keeps recording until the user actually finishes talking — the
  // interruption becomes the next real message, not a truncated fragment.
  const teardownBargeInMonitor = () => {
    if (bargeInTimerRef.current) {
      clearInterval(bargeInTimerRef.current);
      bargeInTimerRef.current = null;
    }
    if (bargeInAudioCtxRef.current) {
      bargeInAudioCtxRef.current.close().catch(() => {});
      bargeInAudioCtxRef.current = null;
    }
    if (bargeInRecorderRef.current) {
      // Same as the stream below — a handed-off recorder is now owned and
      // driven by startHandsFreeSegment; stopping it here would truncate the
      // capture right after handoff, cutting off the rest of what was said.
      if (!bargeInHandedOffRef.current) {
        try {
          if (bargeInRecorderRef.current.state !== 'inactive') bargeInRecorderRef.current.stop();
        } catch (_) { /* ignore */ }
      }
      bargeInRecorderRef.current = null;
    }
    // Ownership of the stream was already transferred to the hf* machinery —
    // stopping it here would kill the recording that was just handed off.
    if (bargeInStreamRef.current && !bargeInHandedOffRef.current) {
      bargeInStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    bargeInStreamRef.current = null;
    bargeInChunksRef.current = [];
    bargeInHandedOffRef.current = false;
  };

  useEffect(() => {
    if (!handsFree || !isSpeaking) {
      teardownBargeInMonitor();
      return undefined;
    }

    let cancelled = false;

    (async () => {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
      } catch (err) {
        console.warn('[Voice] Barge-in monitor could not get mic access (non-fatal):', err?.message || err);
        return;
      }
      if (cancelled || !handsFreeRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      bargeInStreamRef.current = stream;
      bargeInChunksRef.current = [];
      try {
        const mimeType = getSupportedMimeType();
        const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        const recordMime = (recorder.mimeType || mimeType || 'audio/webm').split(';')[0] || 'audio/webm';
        const uploadExt = recordMime.includes('mp4') ? 'mp4' : recordMime.includes('ogg') ? 'ogg' : 'webm';
        bargeInRecorderRef.current = recorder;
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) bargeInChunksRef.current.push(e.data);
        };
        recorder.start(250);
        console.log('[Voice] Barge-in monitor: recording + listening now');

        const Ctx = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new Ctx();
        bargeInAudioCtxRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 1024;
        source.connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);
        let logTick = 0;
        // Same adaptive floor as startHandsFreeSegment. A fixed 0.045 with a
        // run of 450 ms unbroken never fired on a headset: speech dips between
        // syllables reset the run (Lillian's Just talk test, 2026-10-26).
        // Speech in 4 of the last 7 frames (100 ms each) counts as talking.
        let noiseFloor = 0.006;
        const recent = [];
        const WINDOW = 7;
        const VOICED_FRAMES = 4;

        bargeInTimerRef.current = setInterval(() => {
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / buf.length);
          noiseFloor = rms < noiseFloor ? rms : noiseFloor * 0.995 + rms * 0.005;
          const threshold = Math.min(0.10, Math.max(0.025, noiseFloor * 3));
          const voiced = rms > threshold;
          recent.push(voiced);
          if (recent.length > WINDOW) recent.shift();
          const voicedFrames = recent.filter(Boolean).length;
          logTick += 1;
          if (logTick % 5 === 0) {
            console.log('[Voice] Barge-in level', { rms: rms.toFixed(4), threshold: threshold.toFixed(4), voicedFrames });
          }
          if (voiced && voicedFrames >= VOICED_FRAMES) {
            console.log('[Voice] Barge-in TRIGGERED', { rms: rms.toFixed(4) });
            clearInterval(bargeInTimerRef.current);
            bargeInTimerRef.current = null;
            if (!pausePlaybackForBargeIn()) stopPlayback();
            bargeInHandedOffRef.current = true;
            startHandsFreeSegmentRef.current?.({
              existingStream: stream,
              existingRecorder: recorder,
              existingChunks: bargeInChunksRef.current,
              existingMimeType: recordMime,
              existingUploadExt: uploadExt,
            });
          }
        }, 100);
      } catch (err) {
        console.warn('[Voice] Barge-in monitor failed to start (non-fatal):', err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
      teardownBargeInMonitor();
    };
  }, [handsFree, isSpeaking]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const focusChatInput = () => {
    inputRef.current?.focus();
    setInputHighlight(true);
    window.setTimeout(() => setInputHighlight(false), 3500);
  };

  const handleReturningSuggestion = (text) => {
    clearResumeRecap(agentSlug);
    // Respect the Voice toggle — a voice-first user tapping a chip should keep
    // spoken replies rather than being silently switched to text mode.
    const mode = chatModeRef.current || chatMode || 'text';
    startFreshChatWithRecap({ firstUserMessage: text, mode }).then((ok) => {
      if (ok && mode === 'text') focusChatInput();
    });
  };

  const needsBranchChoice = started && conversationNeedsBranchChoice(messages);
  const needsSellerPathChoice = started && conversationNeedsSellerPathChoice(messages);


  const dismissSavePrompt = ({ sessionEnd = false } = {}) => {
    if (agentSlug) {
      try {
        sessionStorage.setItem(savePromptDismissKey(agentSlug), '1');
      } catch { /* ignore */ }
    }
    setSavePromptDismissed(true);
    setPostDismissSaveSignal(false);
    setSaveCardAnchorId(null);
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    if (sessionEnd || chatVisitEnding || threadWindingDownFromMessages(messages)) {
      // Passing on the save card as they leave gets a goodbye, not silence
      // (Lillian's "I'll come back to you" test, 2026-10-26).
      if (!chatVisitEnding) {
        const line = `No problem. Come back whenever you're ready; ${agent?.first_name || 'your agent'} and I will be here.`;
        setMessages((m) => [...m, { role: 'assistant', content: line, id: `notnow-${Date.now()}` }]);
        if (isConversationVoiceMode()) speakLineRef.current?.(line);
      }
      setChatVisitEnding(true);
      if (handsFreeRef.current) stopHandsFree({ silent: true });
      setChatMode('text');
      chatModeRef.current = 'text';
      return;
    }
    // "Before you go" follows a booking too; passing on it should not end the
    // chat. Ava checks if there's anything else (Lillian, 2026-10-25).
    if (/\bBefore you go\b/i.test(lastAssistant?.content || '')) {
      submitUserTextRef.current?.('Not now.');
    }
  };

  const isSellerChat = chatBranch === 'seller' || sellerConsultComplete;

  // Seller consult interstitial only — buyer save-search card is separate.
  const showAccountSavePrompt = useMemo(() => {
    if (client || !started || !agentSlug || savePromptDismissed) return false;
    if (!isSellerChat) return false;
    if (sellerConsultComplete) return true;
    return messages.some(
      (m) => m.role === 'assistant' && (
        m.offer_save_consult
        || /\bwould you like to save (this|your) consult\b/i.test(m.content || '')
        || /\bsave this conversation\b/i.test(m.content || '')
        || /\bSave consult at the top of the page\b/i.test(m.content || '')
      )
    );
  }, [client, started, agentSlug, savePromptDismissed, messages, isSellerChat, sellerConsultComplete]);

  // Numbered options / save-search card — computed before any early return so hooks stay stable.
  const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant');
  const lastAssistantMsgId = lastAssistantMsg?.id;
  const quickReplies = (!isSellerChat && lastAssistantMsg?.quick_replies) || null;
  const areaPromptActive = quickReplies?.kind === 'area_regions';
  const optionChips = quickReplies?.kind === 'options' && Array.isArray(quickReplies.options)
    ? quickReplies.options.filter((o) => o?.label && o?.send)
    : null;
  const areaPickOptions = quickReplies?.kind === 'area_pick' && Array.isArray(quickReplies.options)
    ? quickReplies.options.filter((o) => o?.label && o?.send)
    : null;
  const savedSearchSummary = hasSavedSearch ? formatBuyerCriteriaLine(client?.buyer_profile) : null;
  const sellerPropertySummary = formatSellerPropertyLine(client?.seller_profile);

  // Save-search card (buyer path only) — one-time interstitial after Ava's
  // completed reply. Header "Save search" stays the persistent path.
  const lastThreadMessage = messages.length ? messages[messages.length - 1] : null;
  // Allow a system confirmation (e.g. booking) after Ava without blocking the card.
  const lastIsAssistantComplete = Boolean(
    lastAssistantMsg
    && !sending
    && !resumingChat
    && lastThreadMessage
    && (lastThreadMessage.role === 'assistant'
      || lastThreadMessage.role === 'system'
      || lastThreadMessage.role === 'home_profile')
  );
  const eligibleSaveCriteria = hasMeaningfulSaveCriteria(draftCriteria)
    || hasSearchCriteria
    || countListingsShownInThread(messages) >= 3;
  const explicitSaveOffer = assistantOfferedSaveSearch(lastAssistantMsg);
  const lastAssistantSaveTrigger = explicitSaveOffer || sessionListingSave;
  const showingStillOpen = bookingIntakePending(messages);
  // A message that says "tap the card below" has to get the card, even if an
  // earlier turn in this tab already used the one-time auto-show.
  const canAutoOfferSaveCard = eligibleSaveCriteria
    && !savePromptDismissed
    && lastIsAssistantComplete
    && lastAssistantSaveTrigger
    && !showingStillOpen
    && (!saveCardAutoShown || (explicitSaveOffer && saveCardAnchorId !== lastAssistantMsg?.id));
  const canStrongReofferSaveCard = eligibleSaveCriteria
    && savePromptDismissed
    && postDismissSaveSignal
    && !saveCardStrongReofferUsed
    && lastIsAssistantComplete
    && !showingStillOpen;
  const showSaveSearchCard =
    !client && started && Boolean(agentSlug) && !isSellerChat
    && lastIsAssistantComplete
    && eligibleSaveCriteria
    && !showingStillOpen
    && saveCardAnchorId != null
    && lastAssistantMsg?.id === saveCardAnchorId;
  const saveSearchRecap = savedSearchSummary || formatDraftCriteriaLine(draftCriteria);
  const liveCriteriaLine = formatDraftCriteriaLine(liveCriteria);

  useEffect(() => {
    if (!agentSlug || !lastIsAssistantComplete || !lastAssistantMsg?.id) return;
    if (canAutoOfferSaveCard) {
      setSaveCardAnchorId(lastAssistantMsg.id);
      try {
        sessionStorage.setItem(saveCardAutoShownKey(agentSlug), '1');
      } catch { /* ignore */ }
      setSaveCardAutoShown(true);
      return;
    }
    if (canStrongReofferSaveCard) {
      setSaveCardAnchorId(lastAssistantMsg.id);
      try {
        sessionStorage.setItem(saveCardStrongReofferKey(agentSlug), '1');
      } catch { /* ignore */ }
      setSaveCardStrongReofferUsed(true);
    }
  }, [
    agentSlug,
    lastIsAssistantComplete,
    lastAssistantMsg?.id,
    canAutoOfferSaveCard,
    canStrongReofferSaveCard,
  ]);

  const daylightSwitch = (
    <BuyerDaylightSwitch daylight={daylight} onToggle={toggleDaylight} />
  );
  const testerReset = testerMode ? (
    <button
      type="button"
      className="buyer-agent-strip__reset"
      onClick={resetTesterChat}
      title="Start this demo over"
    >
      Reset
    </button>
  ) : null;
  const buyerPageClass = `buyer-page buyer-page--evening${daylight ? ' buyer-page--day' : ''}`;

  if (agentLoading) {
    return (
      <div className={buyerPageClass}>
        <header className="buyer-agent-strip buyer-agent-strip--gate">
          <div className="buyer-agent-strip__end">
            {testerReset}
            {daylightSwitch}
          </div>
        </header>
        <div className="buyer-gate__body">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <Loader size={32} color="var(--a4a-accent, #C2410C)" />
            <div style={{ color: 'var(--a4a-text-muted, #5A6172)', fontSize: 14 }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (agentError || !agent) {
    const errorCopy = (agentError || 'unreachable') === 'unreachable'
      ? {
        title: 'Cannot reach the assistant',
        body: 'Check your connection, then refresh this page.',
      }
      : agentError === 'not_found'
        ? {
          title: 'Assistant not found',
          body: 'This link may be wrong. Check the address and try again.',
        }
        : {
          title: 'Could not load this assistant',
          body: 'Something went wrong loading this page. Refresh, or try again in a moment.',
        };
    return (
      <div className={buyerPageClass}>
        <header className="buyer-agent-strip buyer-agent-strip--gate">
          <div className="buyer-agent-strip__end">
            {testerReset}
            {daylightSwitch}
          </div>
        </header>
        <div className="buyer-gate__body">
          <div style={{ textAlign: 'center', color: 'var(--a4a-text-muted, #c4b8a8)', maxWidth: 400, padding: 24 }}>
            <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--a4a-text, #f4ede3)', marginBottom: 8 }}>{errorCopy.title}</div>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>{errorCopy.body}</div>
          </div>
        </div>
      </div>
    );
  }

  // Hands-free keeps the mic open most of the time, so typed sends stay enabled
  // there (sendMessage drops the open segment); hold-to-talk still locks send.
  const sendEnabled = input.trim() && !chatBusy && !pipedaBlocked && voiceStatus !== 'transcribing'
    && (voiceStatus !== 'recording' || handsFree);

  const serviceAreaLabel = chatCardBadgeLabel(agent?.service_area_label || agent?.city);
  const HOME_STARTER_GREETING = `I'm ${aiName}, ${agent?.first_name || 'Sarah'}'s assistant, and I'm here to help you find your next home in St. John's or the surrounding areas. If you're selling a home, I can help with that as well. Just tap the button below to get started.`;

  const renderReturningWelcome = () => (
    <div className="buyer-welcome buyer-welcome--returning buyer-welcome--returning-dock">
      <img
        src={getAiAvatarSrc(agent)}
        alt=""
        className="buyer-modepick__avatar"
        onError={handleAvatarFallback}
      />
      <h1 className="buyer-modepick__title">Welcome back, {client.first_name}</h1>
      {newMatchCount > 0 && (
        <button
          type="button"
          className="buyer-welcome__returning-matches"
          onClick={() => handleReturningSuggestion('What homes fit my search?')}
          disabled={chatBusy || resumingChat}
        >
          {newMatchCount} new {newMatchCount === 1 ? 'match' : 'matches'} since you left
        </button>
      )}
      {hasSavedSearch && (
        <p className="buyer-welcome__returning-criteria">
          <span className="buyer-welcome__returning-label">Your search</span>
          {savedSearchSummary}
        </p>
      )}
      {isSellerClient && sellerPropertySummary && (
        <p className="buyer-welcome__returning-criteria">
          <span className="buyer-welcome__returning-label">Your property</span>
          {sellerPropertySummary}
        </p>
      )}
      {isSellerClient && !sellerPropertySummary && !hasSavedSearch && (
        <p className="buyer-welcome__returning-criteria">
          Your seller consult with {agent.first_name} is saved — {aiName} can pick up on your home anytime.
        </p>
      )}
      <p className="buyer-welcome__returning-hint">
        Tap Just talk or type below — {aiName} will open a fresh thread with a quick recap.
      </p>
      <div className="buyer-welcome__chips buyer-welcome__chips--compact" role="list">
        {(client.client_role === 'seller' && !hasSavedSearch
          ? returningSellerSuggestions(agent?.first_name)
          : client.client_role === 'both'
            ? [...RETURNING_SUGGESTIONS.slice(0, 2), returningSellerSuggestions(agent?.first_name)[0]]
            : RETURNING_SUGGESTIONS
        ).map((s, i) => (
          <button
            key={i}
            type="button"
            className="buyer-welcome__chip"
            onClick={() => handleReturningSuggestion(s)}
            disabled={chatBusy || resumingChat}
            role="listitem"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );

  // Entry-state idle greeting: Ava's opening line plus tappable starters. This is
  // the real, interactive idle state — tapping a starter (or typing) begins the chat.
  const renderPreview = () => (
    <div className="buyer-chat__starter">
      <div className="buyer-msg">
        <div className="buyer-msg__avatar buyer-msg__avatar--ai">
          <img src={getAiAvatarSrc(agent)} alt="" onError={handleAvatarFallback} />
        </div>
        <div className="buyer-msg__bubble buyer-msg__bubble--ai">
          {HOME_STARTER_GREETING}
        </div>
      </div>
    </div>
  );

  const renderNewUserWelcome = () => (
    showCriteriaWizard ? (
      <BuyerCriteriaWizard
        agentName={agent?.first_name || 'your agent'}
        aiName={aiName}
        initialDraft={draftCriteria || {}}
        onComplete={handleWizardComplete}
        onSkip={() => setShowCriteriaWizard(false)}
      />
    ) : renderPreview()
  );

  return (
    <div className={`${buyerPageClass}${isConversationView ? ' buyer-page--conversation' : ''}`} style={buyerThemeVars}>
      <div className="buyer-page__orb buyer-page__orb--tr" />
      <div className="buyer-page__orb buyer-page__orb--bl" />

      <div className={`buyer-shell${started || isConversationView ? ' buyer-shell--active' : ''}${isConversationView ? ' buyer-shell--conversation' : ''}`}>
        <header className={`buyer-agent-strip${started || isConversationView ? ' buyer-agent-strip--slim' : ''}${isConversationView ? ' buyer-agent-strip--conversation' : ''}`}>
          <div className="buyer-agent-strip__identity">
            <div className="buyer-agent-strip__avatar">
              {getAgentPhotoSrc(agent) ? (
                <img
                  src={getAgentPhotoSrc(agent)}
                  alt={agentName}
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : agentInitials}
            </div>
            <div>
              <div className="buyer-agent-strip__name">
                {isConversationView && agentSlug ? (
                  <Link to={`/chat/${agentSlug}`} className="buyer-agent-strip__home-link">
                    {agentName}
                  </Link>
                ) : (
                  agentName
                )}
              </div>
              {!isConversationView && (
                <>
                  <div className="buyer-agent-strip__tagline">
                    {agent.brand_tagline || `Licensed REALTOR® · ${agent.brokerage}`}
                  </div>
                  <div className="buyer-agent-strip__meta">
                    <span><MapPin size={11} /> {serviceAreaLabel || agent.city}</span>
                    {agent.brand_website && (
                      <a
                        href={agent.brand_website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="buyer-agent-strip__about"
                      >
                        {aboutLinkLabel} <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                </>
              )}
              {isConversationView && (
                <div className="buyer-agent-strip__tagline">
                  Chat with {aiName}
                </div>
              )}
            </div>
          </div>
          <div className="buyer-agent-strip__end">
            {!client && agentSlug ? (
              <Link
                to={clientAuthPath(agentSlug, { mode: 'login', conversationId: convId })}
                className="buyer-agent-strip__login"
              >
                Log in
              </Link>
            ) : agent.phone ? (
              <a href={`tel:${agent.phone}`} className="buyer-agent-strip__phone">
                <Phone size={13} /> Call {agent.first_name}
              </a>
            ) : null}
            {testerReset}
            {daylightSwitch}
          </div>
        </header>

        {!started && isReturningClient && (
          <div className="buyer-ai-notice" role="note">
            <MessageCircle size={13} className="buyer-ai-notice__icon" />
            <span>
              You&apos;re chatting with <strong>{aiName}</strong>, {agent.first_name}&apos;s AI assistant.
              Details you share are sent to {agent.first_name}, who follows up in person. By continuing you
              agree your information may be stored and shared per our{' '}
              <a href={legalHref('/privacy')} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </span>
          </div>
        )}

        {client && hasSavedSearch && started && (
          <div className="buyer-saved-context">
            <span className="buyer-saved-context__label">Your saved search</span>
            <span className="buyer-saved-context__criteria">{savedSearchSummary}</span>
            <span className="buyer-saved-context__note">{aiName} will use this in this chat.</span>
          </div>
        )}

        {!(client && hasSavedSearch) && started && liveCriteriaLine && (
          <div className="buyer-saved-context">
            <span className="buyer-saved-context__label">Captured so far</span>
            <span className="buyer-saved-context__criteria">{liveCriteriaLine}</span>
          </div>
        )}

        {agentSlug && !clientAuthLoading && client && (
          <Link
            to={`/client/${agentSlug}`}
            className="buyer-client-bar buyer-client-bar--signed-in"
          >
            {client.client_role === 'seller'
              ? 'My seller portal →'
              : client.client_role === 'both'
                ? 'My portal →'
                : 'My saved search →'}
          </Link>
        )}
        <div className={`buyer-hero${!isReturningClient && !isConversationView ? ' buyer-hero--split' : ''}${started || isConversationView ? ' buyer-hero--active' : ''}${isConversationView ? ' buyer-hero--conversation' : ''}`}>
          {!isReturningClient && !isConversationView && (
            <div className="buyer-hero__marketing">
              <h1 className="buyer-hero__title">
                Find your next home, <em>guided every step of the way.</em>
              </h1>
              <ul className="buyer-hero__trust">
                <li className="buyer-hero__trust-row">
                  <span className="buyer-hero__trust-dot" aria-hidden />
                  Local listings &amp; property details
                </li>
                <li className="buyer-hero__trust-row">
                  <span className="buyer-hero__trust-dot" aria-hidden />
                  On {agent.first_name}&apos;s listings, comments direct from the owner
                </li>
                <li className="buyer-hero__trust-row">
                  <span className="buyer-hero__trust-dot" aria-hidden />
                  Replies instantly, any time of day
                </li>
                <li className="buyer-hero__trust-row buyer-hero__trust-row--human">
                  <span className="buyer-hero__trust-dot buyer-hero__trust-dot--human" aria-hidden />
                  A real person, {agent.first_name}, follows up
                </li>
              </ul>
              {agent.crea_connected && (
                <p className="buyer-hero__crea-trust">
                  Listings synced directly from CREA&rsquo;s official data feed, the same source Realtor.ca uses.
                </p>
              )}
              <p className="buyer-hero__no-cost">
                No cost, no obligation, no account needed to start.
              </p>
            </div>
          )}
        <div className="buyer-hero__chat-col">
        <div className={`buyer-chat${isSellerChat ? ' buyer-chat--seller' : ''}`}>
          <div className="buyer-chat__header">
            <img
              src={getAiAvatarSrc(agent)}
              alt=""
              className="buyer-chat__header-avatar"
              onError={handleAvatarFallback}
            />
            <div>
              <div className="buyer-chat__header-title">{agent.first_name}&apos;s AI assistant · {aiName}</div>
              <div className="buyer-chat__header-status">
                <span className="buyer-chat__header-status-dot" />
                Online · Replies instantly
              </div>
            </div>
            {!started && !isConversationView ? (
              <span className="buyer-chat__badge">{serviceAreaLabel ? `${serviceAreaLabel} · Voice & chat` : 'Voice & chat'}</span>
            ) : (
              <>
                <button
                  type="button"
                  className="buyer-chat__new-conv"
                  onClick={startNewConversation}
                  disabled={sending || resumingChat}
                >
                  New conversation
                </button>
                {!client && agentSlug && (
                  isSellerChat && (sellerConsultComplete || showAccountSavePrompt) ? (
                    <Link
                      to={clientAuthPath(agentSlug, {
                        mode: 'register',
                        role: 'seller',
                        conversationId: convId,
                      })}
                      className="buyer-chat__save-anytime"
                    >
                      Save consult
                    </Link>
                  ) : !isSellerChat && hasSearchCriteria ? (
                    <Link
                      to={clientAuthPath(agentSlug, {
                        mode: 'register',
                        conversationId: convId,
                      })}
                      className="buyer-chat__save-anytime"
                    >
                      Save search
                    </Link>
                  ) : null
                )}
                <span className="buyer-chat__badge">{serviceAreaLabel ? `${serviceAreaLabel} · Voice & chat` : 'Voice & chat'}</span>
              </>
            )}
          </div>

          <div ref={messagesContainerRef} onScroll={handleMessagesScroll} className={`buyer-chat__messages${voiceStatus === 'recording' ? ' buyer-chat__messages--recording' : ''}`}>
            {(resumingChat || isConversationView) && messages.length === 0 ? (
              <div className="buyer-chat__resume-loading">
                <Loader size={28} color="var(--a4a-accent, #C2410C)" />
                <p>{isConversationView ? `Loading chat with ${aiName}…` : `Starting chat with ${aiName}…`}</p>
              </div>
            ) : !started && !isConversationView ? (
              isReturningClient ? renderReturningWelcome() : renderNewUserWelcome()
            ) : (
              <div className="buyer-chat__thread">
                {showAccountSavePrompt && isSellerChat && (
                  <div
                    className="buyer-save-prompt"
                    role="region"
                    aria-label="Save your consult"
                  >
                    <Bookmark size={16} className="buyer-save-prompt__icon" aria-hidden />
                    <div className="buyer-save-prompt__copy">
                      <strong>Want to pick this up later?</strong>
                      <span>
                        Free seller account — continue this chat anytime, keep your property details,
                        and track your consultation with {agent.first_name}.
                      </span>
                    </div>
                    <div className="buyer-save-prompt__actions">
                      <Link
                        to={clientAuthPath(agentSlug, {
                          mode: 'register',
                          role: 'seller',
                          conversationId: convId,
                        })}
                        className="buyer-save-prompt__btn"
                      >
                        Save your consult
                      </Link>
                      <button
                        type="button"
                        className="buyer-save-prompt__dismiss"
                        onClick={dismissSavePrompt}
                      >
                        Not now
                      </button>
                    </div>
                  </div>
                )}
                {messages.map(msg => msg.role === 'recap' ? (
                  <div key={msg.id} className="buyer-resume-recap">
                    <div className="buyer-resume-recap__avatar">
                      <img src={getAiAvatarSrc(agent)} alt="" onError={handleAvatarFallback} />
                    </div>
                    <div className="buyer-resume-recap__body">
                      {msg.content.split(/\n\n+/).map((para, i) => (
                        <p key={i}>{para}</p>
                      ))}
                    </div>
                  </div>
                ) : msg.role === 'home_profile' ? (
                  <HomeProfileCard key={msg.id} data={msg.data} agentFirstName={agent.first_name} />
                ) : msg.role === 'system' ? (
                  <div key={msg.id} className="buyer-system-pill">
                    <span>{msg.content}</span>
                  </div>
                ) : (
                  <div
                    key={msg.id}
                    data-msg-id={msg.id}
                    className={`buyer-msg${msg.role === 'user' ? ' buyer-msg--user' : ''}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="buyer-msg__avatar buyer-msg__avatar--ai">
                        <img src={getAiAvatarSrc(agent)} alt="" onError={handleAvatarFallback} />
                      </div>
                    )}
                    <div className={`buyer-msg__bubble${msg.role === 'assistant' ? ' buyer-msg__bubble--ai' : ' buyer-msg__bubble--user'}${msg.role === 'assistant' && msg.featured_properties?.length ? ' buyer-msg__bubble--with-cards' : ''}`}>
                      {msg.role === 'assistant'
                        ? (
                          <MessageContent
                            content={msg.content}
                            featuredProperties={msg.featured_properties}
                            cardVariant={repeatCardIds.has(msg.id) ? 'detail' : undefined}
                            propertyGalleries={msg.property_galleries}
                            canAct={Boolean(agentSlug)}
                            propertyStates={propertyStates}
                            bookedKeys={bookedKeys}
                            onPropertyAction={handlePropertyAction}
                            onPropertyBook={handlePropertyBook}
                            onPropertyTellMore={handlePropertyTellMore}
                            onOptionSelect={
                              msg.id === lastAssistantMsgId && !chatBusy && !resumingChat
                                ? (optionText) => submitUserText(optionText)
                                : undefined
                            }
                          />
                        )
                        : msg.content
                      }
                      {msg.role === 'assistant' && msg.area_links?.length > 0 && (
                        <AreaLinks links={msg.area_links} />
                      )}
                      {msg.role === 'assistant' && msg.booking_picker && msg.id === lastAssistantMsgId && (
                        <BookingPicker
                          picker={msg.booking_picker}
                          agentFirstName={agent?.first_name}
                          disabled={chatBusy || resumingChat}
                          onSubmit={(text) => submitUserText(text, { typedForm: true })}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {showSaveSearchCard && lastIsAssistantComplete && (
                  <SaveSearchCard
                    aiName={aiName}
                    agentFirstName={agent.first_name}
                    agentSlug={agentSlug}
                    conversationId={convId}
                    recap={saveSearchRecap}
                    // No just closes the card. The visit ends only when the buyer
                    // is wrapping up (dismissSavePrompt checks); a heart then No
                    // used to end the whole chat (Lillian, 2026-10-25).
                    onDismiss={() => dismissSavePrompt()}
                  />
                )}

                {needsBranchChoice && !sending && (
                  <div className="buyer-intent-branch" role="group" aria-label="Buying or selling">
                    <button
                      type="button"
                      className="buyer-intent-branch__btn buyer-intent-branch__btn--buy"
                      onClick={() => chooseBranch('buyer')}
                      disabled={chatBusy}
                    >
                      Buying a home
                    </button>
                    <button
                      type="button"
                      className="buyer-intent-branch__btn buyer-intent-branch__btn--sell"
                      onClick={() => chooseBranch('seller')}
                      disabled={chatBusy}
                    >
                      Selling a home
                    </button>
                  </div>
                )}

                {needsSellerPathChoice && !sending && (
                  <div className="buyer-intent-branch" role="group" aria-label="Selling — information or appointment">
                    <button
                      type="button"
                      className="buyer-intent-branch__btn buyer-intent-branch__btn--buy"
                      onClick={() => submitUserText(sellerPathUserMessage('info', agent?.first_name), { fromVoice: false, skipSellerPathChoice: true })}
                      disabled={chatBusy}
                    >
                      Learn about selling
                    </button>
                    <button
                      type="button"
                      className="buyer-intent-branch__btn buyer-intent-branch__btn--sell"
                      onClick={() => submitUserText(sellerPathUserMessage('appointment', agent?.first_name), { fromVoice: false, skipSellerPathChoice: true })}
                      disabled={chatBusy}
                    >
                      Set up an appointment with {agent?.first_name || 'your agent'}
                    </button>
                  </div>
                )}

                {areaPromptActive && !sending && !resumingChat && (
                  <div className="buyer-area-picker" role="group" aria-label="Choose an area">
                    <div className="buyer-area-picker__hint">Tap an area — or type or say your answer</div>
                    <div className="buyer-area-picker__chips">
                      {LOCAL_AREA_COMMUNITIES.map((c) => (
                        <button
                          key={c}
                          type="button"
                          className="buyer-area-picker__chip"
                          onClick={() => submitUserText(`I'm looking in ${c}`, { fromVoice: false })}
                          disabled={chatBusy}
                        >
                          {c}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="buyer-area-picker__chip buyer-area-picker__chip--muted"
                        onClick={() => submitUserText("I'm still open on location — not sure yet", { fromVoice: false })}
                        disabled={chatBusy}
                      >
                        Not sure yet — I'm open
                      </button>
                    </div>
                  </div>
                )}

                {areaPickOptions?.length > 0 && !sending && !resumingChat && (
                  <AreaPickChips
                    key={lastAssistantMsgId}
                    options={areaPickOptions}
                    all={quickReplies.all}
                    disabled={chatBusy}
                    onSubmit={(text) => submitUserText(text, { fromVoice: false })}
                  />
                )}

                {optionChips && !sending && !resumingChat && (
                  <div className="buyer-area-picker" role="group" aria-label="Quick replies">
                    <div className="buyer-area-picker__chips">
                      {optionChips.map((opt) => (
                        <button
                          key={opt.label}
                          type="button"
                          className="buyer-area-picker__chip"
                          onClick={() => submitUserText(opt.send, { fromVoice: false })}
                          disabled={chatBusy}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {sending && (
                  <div className="buyer-msg">
                    <div className="buyer-msg__avatar buyer-msg__avatar--ai">
                      <img src={getAiAvatarSrc(agent)} alt="" onError={handleAvatarFallback} />
                    </div>
                    <div className="buyer-msg__thinking">
                      <span className="buyer-msg__thinking-label">{aiName} is thinking…</span>
                      <div className="buyer-msg__thinking-dots">
                        <span /><span /><span />
                      </div>
                    </div>
                  </div>
                )}

                {chatVisitEnding && !sending && (
                  <div className="buyer-chat__ended" role="status">
                    <p>This chat has ended. You can leave without closing the site.</p>
                    <button type="button" className="buyer-chat__exit buyer-chat__exit--panel" onClick={exitChatVisit}>
                      Exit / End chat
                    </button>
                  </div>
                )}
                <div
                  id="buyer-legal-crea"
                  ref={legalCreaRef}
                  className="buyer-chat__legal-block"
                >
                  {showConversationLegal && (
                    <>
                      <CreaTrademarkNotice className="buyer-footer__trademark" />
                      <EqualHousingNotice className="buyer-footer__trademark" />
                    </>
                  )}
                </div>
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <div className="buyer-chat__pinned">
          {chatVisitEnding && (
            <div className="buyer-chat__exit-dock">
              <button
                type="button"
                className="buyer-chat__exit buyer-chat__exit--panel"
                onClick={exitChatVisit}
              >
                Exit / End chat
              </button>
            </div>
          )}
          {isSpeaking && (
            <div className="buyer-status-bar buyer-status-bar--speak" role="status" aria-live="polite">
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="buyer-status-bar__pulse buyer-status-bar__pulse--speak" />
                {handsFree
                  ? `${aiName} is speaking — tap Skip to jump in`
                  : `${aiName} is speaking…`}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button type="button" className="buyer-btn-quiet" onClick={switchToTextReplies}>
                  Text only
                </button>
                <button type="button" className="buyer-btn-stop" onClick={stopPlayback}>
                  <VolumeX size={14} /> {handsFree ? 'Skip' : 'Stop'}
                </button>
              </span>
            </div>
          )}

          {voiceStatus === 'recording' && (
            <div className="buyer-status-bar buyer-status-bar--record" role="status" aria-live="polite">
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="buyer-status-bar__pulse buyer-status-bar__pulse--record" />
                {handsFree
                  ? (hfCaption
                    ? `“${hfCaption}”`
                    : hfMissNotice
                      ? 'Didn\'t catch that — go ahead and say it again'
                      : 'Listening — just talk; it sends by itself when you stop')
                  : 'Listening… release mic when done'}
              </span>
              {handsFree && (
                <button type="button" className="buyer-btn-stop" onClick={() => stopHandsFree()}>
                  <MicOff size={14} /> Stop talking
                </button>
              )}
            </div>
          )}

          {voiceNotice && !voiceError && (
            <div className="buyer-status-bar buyer-status-bar--speak" role="status">{voiceNotice}</div>
          )}
          {voiceError && (
            <div className="buyer-status-bar buyer-status-bar--error">{voiceError}</div>
          )}

          {(started || isConversationView) && !isSellerChat && !pipedaBlocked && (
            <HomesInChatBar
              homes={homesInChat}
              disabled={chatBusy}
              onTellMore={handlePropertyTellMore}
              onBook={handlePropertyBook}
            />
          )}

          <div className={`buyer-input-dock${!started && !isConversationView ? ' buyer-input-dock--invite' : ''}${!started && !isConversationView && isReturningClient ? ' buyer-input-dock--returning' : ''}${inputHighlight ? ' buyer-input-dock--highlight' : ''}`}>
            {pipedaBlocked && (
              <div className={`buyer-pipeda-notice${sellerAwaitingConsent ? ' buyer-pipeda-notice--seller' : ''}`} role="note">
                <p>
                  Messages and voice you send are stored and shared with {agent.first_name}, who follows up in person.{' '}
                  <a href={legalHref('/privacy')} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
                <p className="buyer-pipeda-notice__reassure">
                  No cost, no obligation, and no account needed to start.
                </p>
                {sellerAwaitingConsent && (
                  <p>Continue to ask {aiName} about selling.</p>
                )}
                <button type="button" className="buyer-pipeda-notice__btn" onClick={acknowledgePipeda}>
                  Approve and get started
                </button>
              </div>
            )}
            {!started && !isConversationView && !pipedaBlocked && (
              <p className="buyer-chat__start-cue">
                Type a question, or tap Just talk
              </p>
            )}
            {!pipedaBlocked && (
            <div className="buyer-composer__row">
            <textarea
              ref={inputRef}
              className={`buyer-input${voiceStatus === 'recording' ? ' buyer-input--recording' : ''}`}
              value={input}
              onChange={e => {
                const next = e.target.value;
                if (!input && next.trim() && voiceStatus === 'playing') stopPlayback();
                setInput(next);
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                voiceStatus === 'recording'
                  ? (handsFree ? "I'm listening — just speak" : 'Listening…')
                  : voiceStatus === 'transcribing'
                    ? 'Got it — one sec…'
                    : handsFree
                      ? "I'm listening — just speak"
                      : needsBranchChoice
                        ? 'Type a question, or Just talk — Hold for one message'
                        : needsSellerPathChoice
                          ? 'Tap Learn about selling or Set up an appointment — or type your choice'
                          : 'Type a message, or Just talk'
              }
              rows={1}
              disabled={voiceStatus === 'transcribing' || resumingChat || pipedaBlocked}
              aria-label="Your message"
            />
            <div className="buyer-input-dock__actions">
              {(chatMode === 'voice' || handsFree) && (
                <button
                  type="button"
                  className="buyer-handsfree-toggle"
                  onClick={switchToTextReplies}
                  title="Quiet answers from here. Tap Just talk when you want Ava to speak again."
                >
                  <span>Text only</span>
                </button>
              )}
              <button
                type="button"
                className={`buyer-handsfree-toggle${handsFree ? ' is-active is-live' : ''}`}
                onClick={toggleHandsFree}
                disabled={pipedaBlocked}
                aria-pressed={handsFree}
                aria-label={handsFree
                  ? 'Listening — tap to stop'
                  : 'Just talk — tap once and keep speaking'}
                title={handsFree
                  ? 'Listening — tap to stop'
                  : 'Just talk — tap once and keep speaking'}
                style={pipedaBlocked ? { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
              >
                <AudioLines size={14} aria-hidden="true" />
                <span>{handsFree ? 'Listening' : 'Just talk'}</span>
              </button>
              <button
                type="button"
                className={`buyer-mic${sendEnabled ? ' buyer-mic--send' : ''}${voiceStatus === 'recording' ? ' buyer-mic--recording' : ''}${!sendEnabled && !handsFree && voiceStatus !== 'transcribing' ? ' buyer-mic--hold' : ''}`}
                disabled={!sendEnabled && micButtonDisabled}
                onClick={sendEnabled ? sendMessage : (handsFree ? toggleHandsFree : undefined)}
                onPointerDown={sendEnabled || handsFree ? undefined : handleMicPointerDown}
                onPointerUp={sendEnabled || handsFree ? undefined : handleMicPointerUp}
                onPointerLeave={sendEnabled || handsFree ? undefined : handleMicPointerUp}
                onPointerCancel={sendEnabled || handsFree ? undefined : handleMicPointerUp}
                onContextMenu={e => e.preventDefault()}
                title={sendEnabled
                  ? 'Send message'
                  : (handsFree
                    ? 'Stop talking'
                    : (isSpeaking ? 'Interrupt Ava — press and hold' : 'Speak one message — press and hold'))}
                aria-label={sendEnabled ? 'Send message' : (handsFree ? 'Stop talking' : (isSpeaking ? 'Interrupt Ava — press and hold' : 'Speak one message — press and hold'))}
              >
                {sendEnabled
                  ? <ArrowUp size={18} className="buyer-send__icon" />
                  : handsFree
                    ? <MicOff size={18} className="buyer-mic__icon" />
                    : voiceStatus === 'transcribing'
                      ? <Loader size={18} className="buyer-mic__icon" />
                      : (
                        <>
                          <Mic size={18} className="buyer-mic__icon" />
                          <span className="buyer-mic__label">Hold</span>
                        </>
                      )}
              </button>
            </div>
            </div>
            )}
            {!started && !isConversationView && !pipedaBlocked && !isReturningClient && !showCriteriaWizard && (
              <p className="buyer-composer__criteria">
                <button
                  type="button"
                  className="buyer-composer__criteria-btn"
                  onClick={() => setShowCriteriaWizard(true)}
                >
                  Fill in your search criteria
                </button>
              </p>
            )}
          </div>

          {(started || isConversationView) && (
            <footer className="buyer-footer buyer-footer--compact" style={{ color: 'var(--a4a-text-muted)' }}>
              <p className="buyer-footer__line">
                Powered by Agents4Agents
                {' · '}Not a substitute for professional real estate or legal advice
                {' · '}<a href={legalHref('/privacy')} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                {' · '}<a href={legalHref('/terms')} target="_blank" rel="noopener noreferrer">Terms</a>
                {' · '}
                <button type="button" className="buyer-footer__legal-btn" onClick={scrollToLegal}>
                  Legal
                </button>
              </p>
            </footer>
          )}
          </div>
        </div>
        </div>

        {!isConversationView && !started && !isReturningClient && (
          <div className="buyer-secondary-actions">
            <button
              type="button"
              className="buyer-hero__seller"
              onClick={() => {
                stopHandsFree({ silent: true });
                handsFreeRef.current = false;
                setHandsFree(false);
                setChatMode('text');
                chatModeRef.current = 'text';
                if (convId) clearHandsFreePreference(agentSlug, convId);
                clearLegacyHandsFreePreference(agentSlug);
                if (pipedaAckedRef.current !== true) {
                  pendingSellerStartRef.current = true;
                  setSellerAwaitingConsent(true);
                  const notice = document.querySelector('.buyer-pipeda-notice');
                  notice?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  notice?.querySelector('button')?.focus();
                  return;
                }
                beginChatRef.current?.({
                  startIntent: 'seller_guided',
                  mode: 'text',
                  forceNew: true,
                });
              }}
              disabled={chatBusy}
            >
              <span className="buyer-hero__seller-icon"><Home size={18} /></span>
              <span className="buyer-hero__seller-text">
                <strong>Thinking of selling?</strong>
                <span>Ask {aiName} about selling, and {agent.first_name} will prepare your market evaluation.</span>
              </span>
              <span className="buyer-hero__seller-arrow" aria-hidden>→</span>
            </button>
            {!client && agentSlug && (
              <Link
                to={clientAuthPath(agentSlug, { mode: 'login', conversationId: convId })}
                className="buyer-hero__returning-btn"
              >
                <span className="buyer-hero__returning-icon"><LogIn size={18} /></span>
                <span className="buyer-hero__returning-text">
                  <strong>Already working with {agent.first_name}?</strong>
                  <span>Log in to pick up where you left off.</span>
                </span>
                <span className="buyer-hero__returning-arrow" aria-hidden>→</span>
              </Link>
            )}
          </div>
        )}
        </div>

        {!isConversationView && !started && (
          <footer className="buyer-footer" style={{ color: 'var(--a4a-text-muted)' }}>
            <p className="buyer-footer__line">
              Powered by <strong>Agents4Agents</strong> · AI assistant for {agentName}.
              Not a substitute for professional real estate or legal advice.
              {' '}<a href={legalHref('/privacy')} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              {' · '}<a href={legalHref('/terms')} target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' · '}
              <button type="button" className="buyer-footer__legal-btn" onClick={() => setShowLandingLegal((v) => !v)}>
                Legal
              </button>
            </p>
            {showLandingLegal && (
              <>
                <CreaTrademarkNotice className="buyer-footer__trademark" />
                <EqualHousingNotice className="buyer-footer__trademark" />
              </>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}
